import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { HonorariosCob } from './HonorariosCob'
import { TelefonesExtra } from './TelefonesExtra'
import { FotoPessoa } from './foto/FotoPessoa'
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiLoader,
  FiCheck,
  FiX,
  FiSend,
  FiEye,
  FiClock,
  FiUsers,
  FiGrid,
  FiTarget,
  FiHash,
  FiCreditCard,
  FiMessageSquare,
  FiChevronDown,
  FiChevronRight,
  FiAlertCircle,
  FiEdit2,
  FiSave,
  FiAward,
  FiRepeat,
  FiFileText,
  FiCopy,
  FiExternalLink,
  FiTrash2,
} from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'
import AssignLeaderModal from './AssignLeaderModal'
import { NEU as neu } from '../lib/neu'

// O fin é focado em cobrança: as seções de aquisição do CRM (Atendimento com IA,
// Campanhas de marketing, Leads encaminhados) ficam DESLIGADAS aqui — o painel
// lateral mostra só Informações + Cobrança + Financeiro. Religar = true (os
// endpoints continuam proxeados; só o fetch/render é pulado).
const SHOW_CRM_SECTIONS = false

// Lead encaminhado a esta líder (seção "Leads encaminhados" da sidebar).
// Espelha o item de /api/leader-followups/by-leader.
interface LeaderLeadItem {
  assignmentId: string; conversationId: string; deviceId: string; leadName: string; leadPhone: string
  assignedAt: string | null; deadlineAt: string | null; hoursLeft: number | null
  status: 'open' | 'acknowledged' | 'overdue' | 'escalated' | 'concluded'
  leaderOutcome: 'success' | 'failure' | 'trying' | null; leaderOutcomeReason: string | null
  convertedOracle: boolean; nudgeCount: number; notes: string | null
  nextNudgeAt: string | null; deadlineNotifiedAt: string | null; autoExpiry: boolean
}
// Próxima ação automática do lead aguardando: lembrete (12h) → aviso de prazo (24h)
// → expirado. Espelha o sweeper. Mesma lógica do painel Líderes (LeaderFollowupPanel).
const fmtDur = (ms: number) => { const h = ms / 3600000; return h >= 1 ? `${Math.round(h)}h` : `${Math.max(1, Math.round(h * 60))}min` }
function nextLeadAction(l: LeaderLeadItem): { text: string; cls: string } | null {
  if (l.status !== 'open' && l.status !== 'overdue') return null
  const now = Date.now()
  const nudgeMs = l.nextNudgeAt ? new Date(l.nextNudgeAt).getTime() : null
  const deadlineMs = l.deadlineAt ? new Date(l.deadlineAt).getTime() : null
  if (l.nudgeCount === 0 && nudgeMs && nudgeMs > now) {
    const d = nudgeMs - now
    return { text: `lembrete em ${fmtDur(d)}`, cls: d < 3 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  if (deadlineMs && deadlineMs > now) {
    const d = deadlineMs - now
    return { text: `aviso de prazo em ${fmtDur(d)}`, cls: d < 6 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  return { text: l.deadlineNotifiedAt ? 'prazo expirado · líder avisada' : 'prazo expirado', cls: 'text-red-600 dark:text-red-400' }
}
const LEAD_STATUS_META: Record<LeaderLeadItem['status'], { label: string; cls: string }> = {
  open:         { label: 'Aguardando', cls: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  acknowledged: { label: 'Em contato', cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  overdue:      { label: 'Atrasado',   cls: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  escalated:    { label: 'Escalado',   cls: 'bg-red-600/20 text-red-700 dark:text-red-300' },
  concluded:    { label: 'Concluído',  cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
}

interface ContactProfile {
  phone: string
  name: string
  type: string
  code: string | null
  cpf: string | null
  email: string | null
  isRegistered: boolean
  isRegisteredInOracle: boolean
  isActive: boolean
  dataSource: 'oracle' | 'mysql' | 'whatsapp'
  activeWeek: string | null
  status: string | null
  hierarchy: {
    district: string; districtCode: string
    group: string; groupCode: string
  } | null
  location: {
    address: string; neighborhood: string
    city: string; zip: string
    lat: number | null; lng: number | null
  } | null
  crm: {
    project_id: string; project_name: string; project_color: string
    column_title: string; column_color: string
    crm_status: string; estimated_value: number | null
    priority: string | null; notes: string | null
    conversation_id: string
  }[]
  campaigns: {
    templateName: string; status: string; campaignStatus: string
    sentAt: string | null; deliveredAt: string | null; readAt: string | null
    error: string | null; date: string
  }[]
  cobrancas?: {
    templateName: string; status: string; campaignStatus: string
    sentAt: string | null; deliveredAt: string | null; readAt: string | null
    error: string | null; date: string
  }[]
  cobrancaStage?: {
    status: string; title: string; color: string | null
    position: number | null; updatedAt: string
  } | null
  messages: {
    total: number; fromContact: number; fromUs: number
    firstMessage: string | null; lastMessage: string | null
  }
  conversations: {
    id: string; deviceId: string; botActive: boolean
    botStatus: string; archived: boolean; lastMessageAt: string
  }[]
}

interface ContactProfilePanelProps {
  phone: string | null
  contactName?: string
  conversationId?: string
}

// Cadastro irmão — outra pessoa cuja celular/tel1/tel2 bate com este número.
// Caso real 2026-05-26: 3 pessoas dividindo (67) 99946-7926 (família).
interface PhoneCadastro {
  codigo: string
  nome: string
  tipo_consultor: string
  status: string
  source_field: 'celular' | 'telefone1' | 'telefone2'
  is_canonical: boolean
}
interface PhoneCadastrosResponse {
  matches: PhoneCadastro[]
  canonical: { codigo: string; nome: string; tipo_consultor: string; source_field: string } | null
  shared: boolean
}

// Boleto Asaas gerado na seção BOLETO ASAAS (só fin, admin) — resposta de
// POST /api/cobranca/asaas/boleto (proxeado pro crm).
interface AsaasBoleto {
  paymentId: string
  installmentId: string | null // parcelamento: id do plano (carnê PDF com todos os boletos)
  parcelas: number
  valorParcela: number
  valorTotal: number
  status: string
  valor: number
  vencimento: string
  bankSlipUrl: string | null
  invoiceUrl: string | null
  linhaDigitavel: string | null // no parcelado, da 1ª parcela
  pixPayload: string | null
  cliente: { codigo: string; nome: string }
}
// Payload que viaja no drag/CustomEvent até o WhatsAppInboxPanel enviar o PDF.
// installmentId presente → baixa o CARNÊ do plano; senão o boleto avulso.
export const ASAAS_BOLETO_MIME = 'application/x-asaas-boleto'
export interface AsaasBoletoSendPayload { paymentId: string; installmentId?: string; fileName: string; caption: string }

// Boleto já existente no Asaas (lista compartilhada — GET /api/asaas/cobrancas):
// quem gerou não importa, todo admin vê os boletos do cliente na conversa.
interface AsaasCobranca {
  paymentId: string; installmentId: string | null; valor: number; vencimento: string
  status: string; description: string | null; invoiceUrl: string | null
  bankSlipUrl: string | null; paymentDate: string | null
}
const COBRANCA_STATUS: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'Em aberto', cls: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  OVERDUE: { label: 'Vencido', cls: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  RECEIVED: { label: 'Pago', cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  CONFIRMED: { label: 'Pago', cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  RECEIVED_IN_CASH: { label: 'Pago', cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  REFUNDED: { label: 'Estornado', cls: 'bg-muted text-muted-foreground' },
}

// Financeiro Oracle do cliente — subconjunto da resposta de /api/cobranca/cliente/:codigo
// que a seção FINANCEIRO consome (lazy, ao expandir). `cliente` entra no jogo
// quando a pessoa ativa é trocada (linha compartilhada): a INFO passa a
// renderizar estes dados em vez do perfil canônico do telefone.
interface FinanceiroData {
  cliente?: {
    codigo: string; nome: string; tipoLabel: string | null; situacao: string | null
    grupo: string | null; distrito: string | null; cidade: string | null
    cep: string | null; cpf: string | null; email: string | null; telefoneFormatado: string | null
  }
  boletosAbertos?: { documento: string; serie: string; vencimento: string | null; valor: number; saldo: number; diasAtraso: number }[]
  totalAberto?: number
  pagamentos?: { documento: string; serie: string; vencimento: string | null; ultPagamento: string | null; valor: number; valorPago: number; saldo: number; quitado: boolean }[]
  pagamentosTotal?: number
}

// Cores canônicas alinhadas com o filter da inbox (WhatsAppInboxPanel:2065-2069)
const TYPE_COLORS: Record<string, string> = {
  'Consultora': 'bg-sky-500',
  'Líder': 'bg-purple-500',
  'Empresário': 'bg-amber-500',
  'Ger. Distribuição': 'bg-stone-500',
  'Novo Lead': 'bg-emerald-500',
  'Contato': 'bg-gray-500',
}

const TYPE_LABELS: Record<string, string> = {
  'Consultora': 'Consultora',
  'Líder': 'Líder',
  'Empresário': 'Empresário',
  'Ger. Distribuição': 'Ger. Distribuição',
  'Novo Lead': 'Novo Lead',
  'Contato': 'Contato',
}

// Metadata interna gravada dentro do JSON lead_extracted_data pelo bot
// (server.cjs:11257,11389). Não é dado do lead — não exibir.
const EXTRACTED_META_KEYS = new Set(['updated_at', 'created_at', 'version', 'source', 'origem'])

const EXTRACTED_LABELS: Record<string, string> = {
  nome: 'Nome', name: 'Nome',
  cpf: 'CPF',
  cep: 'CEP',
  telefone: 'Telefone', phone: 'Telefone', celular: 'Celular',
  email: 'Email', mail: 'Email',
  endereco: 'Endereço', address: 'Endereço',
  numero: 'Número', complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade', city: 'Cidade',
  estado: 'Estado', uf: 'UF',
  data_nascimento: 'Nascimento', nascimento: 'Nascimento',
  interests: 'Interesses', interesses: 'Interesses',
  motivation: 'Motivação', motivacao: 'Motivação',
  profissao: 'Profissão', profession: 'Profissão',
}

const formatExtractedLabel = (k: string): string => {
  const low = k.toLowerCase()
  if (EXTRACTED_LABELS[low]) return EXTRACTED_LABELS[low]
  const spaced = k.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

const formatExtractedValue = (k: string, v: any): string => {
  if (v === null || v === undefined || v === '') return ''
  // Arrays vazios → vazio; arrays com itens → "a · b · c"
  if (Array.isArray(v)) {
    const items = v
      .filter(x => x !== null && x !== undefined && x !== '')
      .map(x => typeof x === 'string' || typeof x === 'number' ? String(x) : JSON.stringify(x))
    return items.length === 0 ? '' : items.join(' · ')
  }
  const raw = typeof v === 'string' || typeof v === 'number' ? String(v) : JSON.stringify(v)
  if (!raw.trim()) return ''
  const low = k.toLowerCase()
  if (low === 'cpf') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 11) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`
  }
  if (low === 'cep') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 8) return `${d.slice(0,5)}-${d.slice(5)}`
  }
  if (low === 'telefone' || low === 'celular' || low === 'phone') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`
    if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`
  }
  if (typeof v === 'string' && ISO_DATETIME_RE.test(v)) {
    const dt = new Date(v)
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
  }
  return raw
}

// Humanização de intent.type (vem em JSON {"type":"...","detail":"..."} —
// server grava livre, snake_case em inglês ou português). Valores observados
// no banco em 2026-05-18 incluem low_engagement, cadastro_consultora,
// pagamento_pendente, information_seeking etc.
const INTENT_LABELS: Record<string, string> = {
  'tráfego_pago': 'Tráfego pago',
  'trafego_pago': 'Tráfego pago',
  'intent_detectado': 'Detecção semântica',
  'midia_inicial': 'Mídia inicial (não-texto)',
  'low_engagement': 'Baixo engajamento',
  'cadastro_consultora': 'Cadastro de consultora',
  'consultora': 'Cadastro de consultora',
  'pagamento_pendente': 'Pagamento pendente',
  'pagamento_cobranca': 'Cobrança',
  'pagamento': 'Pagamento',
  'pagamento_confirmado': 'Pagamento confirmado',
  'confirmacao_pagamento': 'Confirmação de pagamento',
  'information_seeking': 'Buscando informações',
  'information_gathering': 'Aguardando dados do lead',
  'lider_operacional': 'Líder operacional',
  'lider_ativa_convertendo': 'Líder ativa convertendo',
  'unknown': 'Indefinido',
}

const humanizeSnake = (s: string): string => {
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const formatIntent = (intent: string | null): string => {
  if (!intent) return ''
  const i = intent.trim()
  if (INTENT_LABELS[i]) return INTENT_LABELS[i]
  if (i.startsWith('{') || i.startsWith('[')) {
    try {
      const o = JSON.parse(i)
      if (o && typeof o === 'object') {
        const t = String(o.type || o.detail || o.intent || '')
        if (t) return INTENT_LABELS[t] || humanizeSnake(t)
      }
    } catch { /* cai no fallback */ }
  }
  return humanizeSnake(i)
}

const TEMPERATURE_LABELS: Record<string, string> = {
  cold: 'Fria',
  warm: 'Morna',
  hot: 'Quente',
  closing: 'Em fechamento',
}

const formatTemperature = (t: string | null): string => {
  if (!t) return ''
  return TEMPERATURE_LABELS[t.toLowerCase()] || humanizeSnake(t)
}

type AIStage = { id: string; title: string; status: string; color: string; position: number; isCurrent: boolean; isPassed: boolean }
type AITimeline = {
  entrada: { at: string | null }
  dadosCompletos: { at: string | null }
  enviadoLider: { at: string | null; leaderName: string | null; leaderCode: string | null } | null
  convertido: { at: string | null; nome: string | null; codigo: string | null; tipo: string | null } | null
}
type AISession = {
  conversationId: string
  botActive: boolean
  botStatus: string
  intent: string | null
  temperature: string | null
  leadScore: number | null
  extractedData: Record<string, any>
  currentStage: { id: string; title: string; status: string; color: string } | null
  stages: AIStage[]
  progress: { current: number; total: number }
  timeline?: AITimeline
  updatedAt: string
} | null

export function ContactProfilePanel({ phone, contactName, conversationId }: ContactProfilePanelProps) {
  const { authenticatedFetch, user } = useAuth()
  const [profile, setProfile] = useState<ContactProfile | null>(null)
  const [aiSession, setAiSession] = useState<AISession>(null)
  const [loading, setLoading] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['info', 'ai', 'crm', 'leads']))
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({ nome: '', email: '', celular: '', endereco_cidade: '', endereco_bairro: '', endereco_cep: '' })
  const [saving, setSaving] = useState(false)
  // Cadastros que compartilham o número (família, linha de parente, etc.)
  const [phoneCadastros, setPhoneCadastros] = useState<PhoneCadastrosResponse | null>(null)
  // Tel. 2 do bloco de número compartilhado: colapsado por default (geralmente
  // é a rede da líder — 20+ consultoras com o telefone dela como secundário,
  // pouco útil pro operador). Celular/tel. 1 aparecem direto (são conflito real).
  const [sharedTel2Expanded, setSharedTel2Expanded] = useState(false)
  // Financeiro (débitos abertos + pagamentos) — lazy: só busca quando a seção
  // FINANCEIRO é expandida (Oracle é pesado/oscila). Resetado ao trocar de contato.
  const [financeiro, setFinanceiro] = useState<FinanceiroData | null>(null)
  const [financeiroLoading, setFinanceiroLoading] = useState(false)
  const [financeiroError, setFinanceiroError] = useState<string | null>(null)
  // Código atualmente em voo/carregado — guarda contra refetch e descarta
  // respostas obsoletas ao trocar de contato. NÃO usar `loading` como dep de
  // effect (re-roda e auto-cancela a própria request → loading eterno).
  const financeiroReqRef = useRef<string | null>(null)
  // Leads encaminhados a ESTA líder (se o contato for líder com leads). Self-gating:
  // busca por profile.code; vazio pra não-líder → seção não aparece. É o fallback
  // manual de baixa quando o fluxo automático (template/nudge) falha.
  const [leaderLeads, setLeaderLeads] = useState<LeaderLeadItem[]>([])
  const [leaderCounts, setLeaderCounts] = useState<{ total: number; pending: number; concluded: number } | null>(null)
  const [leaderLeadsLoading, setLeaderLeadsLoading] = useState(false)
  const [leadActionBusy, setLeadActionBusy] = useState<string | null>(null)
  const [leadReason, setLeadReason] = useState<Record<string, string>>({})
  // Leads CONCLUÍDOS em modo "alterar desfecho" (ex.: líder marcou "deu certo" mas o
  // cliente desistiu → reverter pra "não deu"/"ainda tentando"). Keyed por assignmentId.
  const [editingOutcome, setEditingOutcome] = useState<Set<string>>(new Set())
  const toggleEditOutcome = (id: string) => setEditingOutcome(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const [leadError, setLeadError] = useState<string>('')
  const [leadInfo, setLeadInfo] = useState<string>('')
  const [showConcludedLeads, setShowConcludedLeads] = useState(false)
  // Trocar líder: lead a ser reatribuído a outra líder (abre o AssignLeaderModal).
  const [reassignLead, setReassignLead] = useState<LeaderLeadItem | null>(null)

  // ── Boleto Asaas (só no fin, admin-only — o endpoint do crm exige admin) ──
  // Estado vive por conversa: trocar de contato reseta form e card.
  const ymdLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const boletoDefaultVenc = () => ymdLocal(new Date(Date.now() + 3 * 86400000))
  // Parse de valor: vírgula = decimal pt-BR; ponto só é decimal com 1-2 casas
  // ("1155.03"); senão é milhar ("1.234"). O confirm abaixo expõe o valor
  // parseado antes de criar — cobrança é real, digitação errada não pode
  // passar em silêncio (pente-fino 2026-07-20).
  const parseValorBR = (s: string): number => {
    const t = s.trim()
    if (!t) return NaN
    if (t.includes(',')) return Number(t.replace(/\./g, '').replace(',', '.'))
    if (/^\d+\.\d{1,2}$/.test(t)) return Number(t)
    return Number(t.replace(/\./g, ''))
  }
  // 04/11/2026 — negociação com ENTRADA (o diretor viu no cob.twms e pediu igual):
  // total do acordo − entrada = restante (1 boleto ou carnê). Mesmo fluxo do BoletoCob.jsx.
  const formVazio = (cliente = '') => ({ cliente, valor: '', entrada: '', vencEntrada: boletoDefaultVenc(), vencimento: boletoDefaultVenc(), descricao: '', parcelas: '1' })
  const [boletoForm, setBoletoForm] = useState(formVazio())
  const [boletoBusy, setBoletoBusy] = useState(false)
  const [boletoError, setBoletoError] = useState<string | null>(null)
  // Boletos gerados nesta negociação (com entrada saem DOIS: entrada + restante).
  const [gerados, setGerados] = useState<Array<AsaasBoleto & { rotulo: string | null }>>([])
  const valorTocadoRef = useRef(false) // já mexeu no valor? então não sobrescreve com o atualizado
  const [boletoCopied, setBoletoCopied] = useState<string | null>(null)

  // Guarda contra corrida: resposta de geração disparada na conversa A não
  // pode "vazar" pro card da conversa B (o PDF iria pro cliente errado).
  // boletoSelRef cobre a variante DENTRO da mesma conversa: trocar a PESSOA
  // ativa (linha compartilhada) durante o request também descarta a resposta.
  const boletoPhoneRef = useRef(phone)
  boletoPhoneRef.current = phone
  const boletoSelRef = useRef<string | null>(null)
  useEffect(() => {
    // Reset ao trocar de conversa; o código do cliente pré-preenche quando o
    // perfil resolve (editável — a conversa pode não casar com cadastro).
    setGerados([]); setBoletoError(null); setBoletoBusy(false)
    setBoletoForm(formVazio()); valorTocadoRef.current = false
  }, [phone])
  useEffect(() => {
    if (profile?.code) setBoletoForm(f => (f.cliente ? f : { ...f, cliente: String(profile.code) }))
  }, [profile?.code])

  // ── Alvo do boleto ≠ pessoa da conversa (caso Rogério→Márcia 2026-07-20):
  // o campo Cliente é independente — digitar outro código/CPF/nome resolve o
  // cliente no DRACO e mostra nome + dívida DELE (o gerar já era independente;
  // faltava o preview). Debounce 500ms; múltiplos matches viram lista de pick.
  interface BoletoAlvo { codigo: string; nome: string; tipoLabel: string | null; cpf: string | null; cidade: string | null }
  const [boletoAlvo, setBoletoAlvo] = useState<BoletoAlvo | null>(null)
  const [boletoAlvoMatches, setBoletoAlvoMatches] = useState<BoletoAlvo[]>([])
  const [boletoAlvoLoading, setBoletoAlvoLoading] = useState(false)
  const [boletoAlvoVazio, setBoletoAlvoVazio] = useState(false) // lookup rodou e não achou ninguém
  const boletoAlvoReqRef = useRef('')
  useEffect(() => {
    const q = boletoForm.cliente.trim()
    const own = boletoSelRef.current // effCode da pessoa ativa (setado no render)
    // vazio ou igual à pessoa ativa → sem alvo alternativo
    if (!q || q === own || (own && q.replace(/\D/g, '') === own)) {
      setBoletoAlvo(null); setBoletoAlvoMatches([]); setBoletoAlvoLoading(false); setBoletoAlvoVazio(false)
      boletoAlvoReqRef.current = ''
      return
    }
    if (q.length < 3) { setBoletoAlvo(null); setBoletoAlvoMatches([]); setBoletoAlvoVazio(false); return }
    boletoAlvoReqRef.current = q
    setBoletoAlvoLoading(true); setBoletoAlvoVazio(false)
    const t = setTimeout(async () => {
      try {
        const r = await authenticatedFetch(`/api/cobranca/cliente-busca?q=${encodeURIComponent(q)}`)
        const d = await r.json()
        if (boletoAlvoReqRef.current !== q) return // resposta obsoleta
        const matches: BoletoAlvo[] = (r.ok && d.success ? d.matches : []) || []
        if (matches.length === 1) { setBoletoAlvo(matches[0]); setBoletoAlvoMatches([]) }
        else { setBoletoAlvo(null); setBoletoAlvoMatches(matches) }
        setBoletoAlvoVazio(matches.length === 0)
      } catch { if (boletoAlvoReqRef.current === q) { setBoletoAlvo(null); setBoletoAlvoMatches([]) } }
      finally { if (boletoAlvoReqRef.current === q) setBoletoAlvoLoading(false) }
    }, 500)
    return () => clearTimeout(t)
  }, [boletoForm.cliente, authenticatedFetch])

  // Total − entrada = restante, em 1 boleto ou N parcelas mensais (carnê).
  const PARCELAS_OPCOES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15, 18, 24, 36, 48, 60]
  const plano = ((): { erro: string | null; total: number; entrada: number; restante: number; n: number; parcela: number } => {
    const vazio = (erro: string | null) => ({ erro, total: 0, entrada: 0, restante: 0, n: 1, parcela: 0 })
    const total = parseValorBR(boletoForm.valor)
    const entradaTxt = boletoForm.entrada.trim()
    const entrada = entradaTxt ? parseValorBR(entradaTxt) : 0
    const n = Math.max(1, Math.min(60, Math.trunc(Number(boletoForm.parcelas) || 1)))
    if (!Number.isFinite(total) || !(total > 0)) return vazio(boletoForm.valor.trim() ? 'Valor total inválido' : null)
    if (!Number.isFinite(entrada) || entrada < 0) return vazio('Valor da entrada inválido')
    if (entrada >= total) return vazio('A entrada tem que ser menor que o total. Sem parcelas? Deixe a entrada vazia e use 1 parcela.')
    if (entrada > 0 && entrada < 5) return vazio('Entrada mínima de R$ 5,00 (regra do Asaas)')
    const restante = Math.round((total - entrada) * 100) / 100
    const parcela = Math.ceil((restante / n) * 100 - 1e-9) / 100
    if (restante / n < 5) return vazio('Cada parcela tem que ser de pelo menos R$ 5,00 — diminua as parcelas')
    if (entrada > 0 && boletoForm.vencimento <= boletoForm.vencEntrada) return vazio('A 1ª parcela tem que vencer DEPOIS da entrada')
    return { erro: null, total, entrada, restante, n, parcela }
  })()

  const gerarBoleto = async () => {
    if (boletoBusy) return
    setBoletoError(null)
    const reqPhone = phone
    const reqSel = boletoSelRef.current
    try {
      // Nome digitado com alvo resolvido → usa o código do alvo (o campo pode
      // conter texto); senão, os dígitos do que foi digitado (código/CPF).
      const digits = (boletoAlvo?.codigo || boletoForm.cliente).replace(/\D/g, '')
      if (!digits) throw new Error('Informe o código do cliente ou CPF')
      if (!plano.total) throw new Error(plano.erro || 'Valor inválido')
      const { total, entrada, restante, n, parcela } = plano
      const desc = boletoForm.descricao.trim()
      const pecas: Array<{ rotulo: string | null; valor: number; parcelas: number; vencimento: string; descricao: string }> = []
      if (entrada > 0) pecas.push({ rotulo: 'Entrada', valor: entrada, parcelas: 1, vencimento: boletoForm.vencEntrada, descricao: desc || 'Entrada do acordo' })
      pecas.push({ rotulo: entrada > 0 ? 'Restante' : null, valor: restante, parcelas: n, vencimento: boletoForm.vencimento, descricao: desc || (entrada > 0 ? 'Restante do acordo' : '') })

      // Cobrança REAL no Asaas: confirmar mostrando os valores JÁ PARSEADOS.
      const alvoTxt = boletoAlvo ? `${boletoAlvo.nome} (#${boletoAlvo.codigo})` : `cliente ${digits}`
      const linhas = [`Confirma a negociação de ${alvoTxt}?`, '', `Total do acordo: ${fmtCurrency(total)}`]
      if (entrada > 0) linhas.push(`• Entrada: ${fmtCurrency(entrada)} — vence ${fmtVenc(boletoForm.vencEntrada)}`)
      linhas.push(n > 1
        ? `• ${entrada > 0 ? 'Restante em ' : ''}${n}x de ${fmtCurrency(parcela)} — 1ª vence ${fmtVenc(boletoForm.vencimento)}, depois todo mês`
        : `• ${entrada > 0 ? 'Restante' : 'Boleto único'}: ${fmtCurrency(restante)} — vence ${fmtVenc(boletoForm.vencimento)}`)
      linhas.push('', `Vão ser gerados ${pecas.length === 2 ? 'DOIS boletos (entrada + restante)' : n > 1 ? 'um carnê' : 'um boleto'} no Asaas.`)

      // [2026-08-22] Trava de duplicata. O Asaas aceita alegremente N cobranças
      // idênticas pro mesmo sacado; em 22/08 saíram 4 parcelamentos iguais pra
      // mesma cliente. Confere cada boleto que vai sair na lista já carregada.
      const aberto = (cobrancas || []).filter(c => c.status === 'PENDING' || c.status === 'OVERDUE')
      const grupos = new Map<string, { total: number; n: number }>()
      for (const c of aberto) {
        const k = c.installmentId || `avulso:${c.paymentId}`
        const g = grupos.get(k) || { total: 0, n: 0 }
        g.total += Number(c.valor) || 0; g.n += 1
        grupos.set(k, g)
      }
      // Compara em centavos: soma de floats erra no último dígito.
      const iguais = [...grupos.values()].filter(g => pecas.some(pc => Math.round(g.total * 100) === Math.round(pc.valor * 100)))
      let aviso = ''
      if (iguais.length) {
        aviso = `\n\n⚠️ ATENÇÃO: este cliente JÁ TEM cobrança/parcelamento EM ABERTO com esse mesmo valor`
          + ` (${iguais.map(g => g.n > 1 ? `${g.n}×` : 'avulso').join(', ')}).`
          + `\nGerar de novo COBRA O CLIENTE DUAS VEZES. Só confirme se for mesmo uma cobrança adicional.`
      } else if (cobrancasErro) {
        aviso = `\n\n⚠️ Não consegui conferir os boletos já emitidos deste cliente (${cobrancasErro}).`
          + `\nPode haver cobrança repetida sem eu ver. Confira no Asaas antes de confirmar.`
      }
      if (!window.confirm(linhas.join('\n') + aviso)) return
      setBoletoBusy(true)
      const feitos: Array<AsaasBoleto & { rotulo: string | null }> = []
      for (const pc of pecas) {
        const body: Record<string, unknown> = { valor: pc.valor, vencimento: pc.vencimento }
        if (pc.parcelas > 1) body.parcelas = pc.parcelas
        if (digits.length >= 11) body.cpf = digits; else body.codigo = digits
        if (pc.descricao) body.descricao = pc.descricao
        const r = await authenticatedFetch('/api/cobranca/asaas/boleto', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok || !d.success) {
          if (feitos.length && boletoPhoneRef.current === reqPhone) {
            // A entrada saiu e o restante não: mostra a entrada e deixa o
            // formulário pronto só com o restante, para tentar de novo.
            setGerados(g => [...g, ...feitos])
            setBoletoForm(f => ({ ...f, valor: restante.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), entrada: '' }))
            valorTocadoRef.current = true
            throw new Error(`A ENTRADA foi gerada, mas o restante falhou (${d.error || 'erro no Asaas'}). O formulário ficou com o restante — confira e clique em Gerar de novo.`)
          }
          throw new Error(d.error || 'Falha ao gerar o boleto')
        }
        feitos.push({ ...d, rotulo: pc.rotulo })
      }
      if (boletoPhoneRef.current !== reqPhone || boletoSelRef.current !== reqSel) return // trocou de conversa OU de pessoa — descarta
      setGerados(g => [...g, ...feitos])
      if (cobrancasDocRef.current) loadCobrancas(cobrancasDocRef.current) // lista compartilhada reflete na hora
    } catch (e: any) { if (boletoPhoneRef.current === reqPhone && boletoSelRef.current === reqSel) setBoletoError(e.message) }
    finally { if (boletoPhoneRef.current === reqPhone && boletoSelRef.current === reqSel) setBoletoBusy(false) }
  }

  const cancelarBoleto = async (boleto: AsaasBoleto) => {
    if (!boleto || boletoBusy) return
    const msg = boleto.installmentId
      ? `Cancelar o parcelamento inteiro (${boleto.parcelas} boletos, total ${fmtCurrency(boleto.valorTotal)}) de ${boleto.cliente.nome}?`
      : `Cancelar o boleto de ${fmtCurrency(boleto.valor)} de ${boleto.cliente.nome}?`
    if (!window.confirm(msg)) return
    setBoletoBusy(true); setBoletoError(null)
    try {
      const url = boleto.installmentId
        ? `/api/cobranca/asaas/parcelamento/${boleto.installmentId}`
        : `/api/cobranca/asaas/boleto/${boleto.paymentId}`
      const r = await authenticatedFetch(url, { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao cancelar')
      setGerados(g => g.filter(x => x.paymentId !== boleto.paymentId))
      if (cobrancasDocRef.current) loadCobrancas(cobrancasDocRef.current)
    } catch (e: any) { setBoletoError(e.message) }
    finally { setBoletoBusy(false) }
  }

  const boletoCopy = async (key: string, text: string | null) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setBoletoCopied(key)
      setTimeout(() => setBoletoCopied(c => (c === key ? null : c)), 1500)
    } catch { /* clipboard bloqueado */ }
  }

  // ── Troca de pessoa ativa (linha compartilhada — caso Kemily×Lucimar,
  // pedido do diretor 2026-07-20): o telefone resolve pro cadastro CANÔNICO,
  // mas a negociação pode ser com OUTRA pessoa do mesmo número. Selecionar
  // um cadastro no bloco "📞 N cadastros" troca INFO + FINANCEIRO + Boleto
  // Asaas pra ela; clicar de novo (ou na DONA) volta ao canônico.
  const [altCadastro, setAltCadastro] = useState<{ codigo: string; nome: string } | null>(null)
  useEffect(() => { setAltCadastro(null) }, [phone])
  const effCode = altCadastro?.codigo || (profile?.code ? String(profile.code) : null)
  boletoSelRef.current = effCode // guarda de corrida do gerarBoleto (pessoa ativa)
  const selectCadastro = (codigo: string, nome: string, isCanonical: boolean) => {
    const next = isCanonical || altCadastro?.codigo === codigo ? null : { codigo, nome }
    setAltCadastro(next)
    // financeiro/dívida são por código — força refetch pro novo alvo
    financeiroReqRef.current = null
    setFinanceiro(null); setFinanceiroError(null)
    // boleto: o sacado mudou — reseta card/erro e pré-preenche o código
    setGerados([]); setBoletoError(null); valorTocadoRef.current = false
    setBoletoForm(f => ({ ...f, cliente: next ? next.codigo : (profile?.code ? String(profile.code) : ''), valor: '', entrada: '' }))
  }

  // ── Boletos JÁ emitidos no Asaas pra pessoa ativa (por CPF, direto da API —
  // fresco e compartilhado: o Ricardo gera na sessão dele, aparece pra todos).
  const [cobrancas, setCobrancas] = useState<AsaasCobranca[] | null>(null)
  const [cobrancasLoading, setCobrancasLoading] = useState(false)
  // [2026-08-22] Falha de consulta PRECISA ser distinta de "não tem boleto".
  // Antes, qualquer erro virava `setCobrancas([])` e a seção inteira sumia da
  // tela — com a chave do Asaas quebrada (401), o Ricardo via a lista vazia,
  // concluía que o boleto não tinha sido criado e gerava outro. Saíram 4
  // parcelamentos duplicados pra mesma cliente. Erro agora aparece na cara.
  const [cobrancasErro, setCobrancasErro] = useState<string | null>(null)
  const cobrancasDocRef = useRef<string | null>(null)
  const loadCobrancas = useCallback(async (doc: string) => {
    cobrancasDocRef.current = doc
    setCobrancasLoading(true)
    setCobrancasErro(null)
    try {
      const r = await authenticatedFetch(`/api/asaas/cobrancas?doc=${encodeURIComponent(doc)}`)
      const d = await r.json()
      if (cobrancasDocRef.current !== doc) return
      if (r.ok && d.success) { setCobrancas(d.cobrancas); setCobrancasErro(null) }
      // null (não []) de propósito: lista vazia é uma AFIRMAÇÃO de que não há
      // boleto, e é justamente o que não sabemos quando a consulta falha.
      else { setCobrancas(null); setCobrancasErro(d?.error || `Falha ao consultar o Asaas (HTTP ${r.status})`) }
    } catch (e: any) {
      if (cobrancasDocRef.current === doc) { setCobrancas(null); setCobrancasErro(e?.message || 'Falha de rede ao consultar o Asaas') }
    }
    finally { if (cobrancasDocRef.current === doc) setCobrancasLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { setCobrancas(null); setCobrancasErro(null); cobrancasDocRef.current = null }, [phone])
  useEffect(() => {
    if (!expandedSections.has('boleto')) return
    const doc = String((altCadastro ? financeiro?.cliente?.cpf : profile?.cpf) || '').replace(/\D/g, '')
    if (doc.length < 11) return
    if (cobrancasDocRef.current === doc) return
    loadCobrancas(doc)
  }, [expandedSections, altCadastro, financeiro?.cliente?.cpf, profile?.cpf, loadCobrancas])

  // Honorários advocatícios (13/10/2026, pedido do diretor — igual ao cob.twms):
  // chave POR NEGOCIAÇÃO. Desliga sozinha ao trocar de conversa ou de pessoa —
  // senão a próxima dívida sairia com honorários sem ninguém ter decidido.

  // Parâmetros de multa/juros do boleto (charge_config do fin — ajustável em
  // Cadastros → Contas e Cobrança). Alimenta o "valor atualizado" da dívida.
  const [chargeCfg, setChargeCfg] = useState<{ multaPct: number; jurosDia: number; honorariosPct: number } | null>(null)
  useEffect(() => {
    authenticatedFetch('/api/settings/conta-pj').then(r => r.json())
      .then(d => {
        if (d?.charge) setChargeCfg({
          multaPct: Number(d.charge.boleto_multa_pct) || 0,
          jurosDia: Number(d.charge.boleto_juros_dia) || 0,
          honorariosPct: Number(d.charge.honorarios_pct) || 0,
        })
      })
      .catch(() => {})
  }, [authenticatedFetch])

  // Valor total já vem preenchido com o ATUALIZADO (igual ao cob, 04/11) — continua
  // editável para desconto. Só preenche enquanto ninguém mexeu no campo.
  const totalAtualizadoAtual = (() => {
    if (!financeiro || !chargeCfg) return null
    const t = (financeiro.boletosAbertos || []).reduce((acc, b) =>
      acc + (b.diasAtraso > 0 ? b.saldo * (1 + chargeCfg.multaPct / 100 + (chargeCfg.jurosDia / 100) * b.diasAtraso) : b.saldo), 0)
    return Math.round(t * 100) / 100
  })()
  useEffect(() => {
    if (valorTocadoRef.current || gerados.length || !totalAtualizadoAtual || !(totalAtualizadoAtual > 0)) return
    setBoletoForm(f => ({ ...f, valor: totalAtualizadoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }))
  }, [totalAtualizadoAtual, gerados.length])

  const boletoSendPayload = (b: AsaasBoleto): AsaasBoletoSendPayload => (b.installmentId ? {
    paymentId: b.paymentId,
    installmentId: b.installmentId,
    fileName: `carne-${b.cliente.codigo}-${b.parcelas}x.pdf`,
    caption: `Carnê ${b.parcelas}× de ${fmtCurrency(b.valorParcela)} (total ${fmtCurrency(b.valorTotal)}) · 1ª parcela vence ${fmtVenc(b.vencimento)}`,
  } : {
    paymentId: b.paymentId,
    fileName: `boleto-${b.cliente.codigo}-${b.vencimento}.pdf`,
    caption: `Boleto ${fmtCurrency(b.valor)} · vence ${fmtVenc(b.vencimento)}${b.linhaDigitavel ? `\nLinha digitável: ${b.linhaDigitavel}` : ''}`,
  })

  const loadProfile = useCallback(async (phoneNumber: string) => {
    setLoading(true)
    setFinanceiro(null); setFinanceiroError(null); financeiroReqRef.current = null  // novo contato → invalida financeiro
    try {
      const [r, rSession] = await Promise.all([
        authenticatedFetch(`/api/contact-profile/${phoneNumber}`),
        SHOW_CRM_SECTIONS ? authenticatedFetch(`/api/contacts/${phoneNumber}/ai-session`) : Promise.resolve(null)
      ])
      const d = await r.json()
      if (d.success) setProfile(d.profile)
      else setProfile(null)
      try {
        const ds = rSession ? await rSession.json() : null
        setAiSession(ds?.session || null)
      } catch { setAiSession(null) }
    } catch { setProfile(null); setAiSession(null) }
    finally { setLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => {
    if (phone) {
      const clean = phone.replace(/\D/g, '').replace(/@.*/, '')
      if (clean.length >= 8) loadProfile(clean)
      else { setProfile(null); setAiSession(null) }
    } else {
      setProfile(null)
      setAiSession(null)
    }
  }, [phone, loadProfile])

  // Detectar números compartilhados — endpoint só responde quando a conv tem
  // remote_jid resolvível. Falha silenciosa: badge só renderiza se shared=true.
  useEffect(() => {
    if (!conversationId) { setPhoneCadastros(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const r = await authenticatedFetch(`/api/conversations/${encodeURIComponent(conversationId)}/phone-cadastros`)
        if (!r.ok) { if (!cancelled) setPhoneCadastros(null); return }
        const d: PhoneCadastrosResponse = await r.json()
        if (!cancelled) setPhoneCadastros(d)
      } catch {
        if (!cancelled) setPhoneCadastros(null)
      }
    })()
    return () => { cancelled = true }
  }, [conversationId, authenticatedFetch])

  // Refresh quando AssignLeaderModal completa a atribuição — caso contrário a
  // timeline "Enviado para líder" continuaria "pendente" até trocar de conversa.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const cleanCurrent = (phone || '').replace(/\D/g, '').replace(/@.*/, '')
      const cleanEvent = String(detail.phone || '').replace(/\D/g, '')
      // Refresh se o evento é da conversa atual (ou se phone não foi enviado)
      if (!detail.phone || cleanEvent === cleanCurrent) {
        if (cleanCurrent.length >= 8) loadProfile(cleanCurrent)
      }
    }
    window.addEventListener('leaderAssigned', handler)
    return () => window.removeEventListener('leaderAssigned', handler)
  }, [phone, loadProfile])

  const toggleSection = (s: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s); else next.add(s)
      return next
    })
  }

  // Carrega o financeiro de um código. Marca o ref ANTES do await; ao resolver,
  // só aplica o resultado se o ref ainda aponta pra esse código (descarta resposta
  // obsoleta de um contato anterior). Reusado pelo effect e pelo "tentar de novo".
  const loadFinanceiro = useCallback(async (code: string) => {
    financeiroReqRef.current = code
    setFinanceiroLoading(true); setFinanceiroError(null)
    try {
      const r = await authenticatedFetch(`/api/cobranca/cliente/${encodeURIComponent(code)}`)
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `Erro ${r.status}`) }
      const d: FinanceiroData = await r.json()
      if (financeiroReqRef.current === code) setFinanceiro(d)
    } catch (err) {
      if (financeiroReqRef.current === code) { setFinanceiroError(err instanceof Error ? err.message : 'Erro'); financeiroReqRef.current = null }
    } finally {
      if (financeiroReqRef.current === code || financeiroReqRef.current === null) setFinanceiroLoading(false)
    }
  }, [authenticatedFetch])

  // Lazy-fetch: dispara UMA vez por código quando a seção FINANCEIRO ou a
  // BOLETO ASAAS é expandida (a saúde da dívida vive lá). Código ativo pode
  // ser o do cadastro TROCADO (altCadastro — linha compartilhada); nesse caso
  // dispensa o gate isOracle (os matches sempre vêm do DRACO). `loading` NÃO
  // entra nas deps (evita o loop que auto-cancelava a request).
  useEffect(() => {
    const isOracle = !!profile?.code && (profile?.isRegisteredInOracle || profile?.dataSource === 'oracle')
    // Prioridade: alvo digitado no campo Cliente do boleto (pode ser QUALQUER
    // cliente) > pessoa ativa trocada (altCadastro) > perfil canônico. Um único
    // effect governa o `financeiro` — dois effects competindo deixavam a dívida
    // de P na tela com o rótulo do alvo A (pente-fino 2026-07-20).
    const code = boletoAlvo?.codigo || altCadastro?.codigo || (isOracle ? String(profile!.code) : null)
    // 13/10/2026: deixou de ser preguiçoso. O bloco "Com honorários advocatícios" fica
    // SEMPRE à vista no topo (pedido do diretor), e precisa da dívida assim que a conversa abre.
    if (!code) return
    if (financeiroReqRef.current === code) return
    loadFinanceiro(code)
  }, [expandedSections, profile?.code, profile?.isRegisteredInOracle, profile?.dataSource, altCadastro, boletoAlvo, loadFinanceiro])

  // Busca os leads encaminhados a esta líder. Reusado pelo effect e pelos refresh
  // pós-ação. profile.code é o codigo do cadastro = leader_code nas atribuições.
  const loadLeaderLeads = useCallback(async (code: string) => {
    setLeaderLeadsLoading(true)
    try {
      const r = await authenticatedFetch(`/api/leader-followups/by-leader?leaderCode=${encodeURIComponent(code)}`)
      const d = await r.json()
      if (r.ok && d.success) { setLeaderLeads(d.items || []); setLeaderCounts(d.counts || null) }
      else { setLeaderLeads([]); setLeaderCounts(null) }
    } catch { setLeaderLeads([]); setLeaderCounts(null) }
    finally { setLeaderLeadsLoading(false) }
  }, [authenticatedFetch])

  // Ao trocar de contato: reseta e (se há code) busca. Rascunhos de motivo/nota
  // são por assignmentId, mas reseta mesmo assim pra não vazar entre contatos.
  useEffect(() => {
    const code = profile?.code
    setLeadReason({}); setLeadError(''); setShowConcludedLeads(false)
    if (!code || !SHOW_CRM_SECTIONS) { setLeaderLeads([]); setLeaderCounts(null); return }
    loadLeaderLeads(code)
  }, [profile?.code, loadLeaderLeads])

  // POST de ação manual (outcome|note) + refresh. authMiddleware no backend.
  const leadAction = useCallback(async (assignmentId: string, path: string, body: any) => {
    setLeadActionBusy(assignmentId); setLeadError(''); setLeadInfo('')
    try {
      const r = await authenticatedFetch(`/api/leader-followups/${encodeURIComponent(assignmentId)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.success === false) throw new Error(d.error || 'Erro ao salvar')
      // Cobrança/aviso entram numa FILA throttada (3 por líder a cada 2 min) — não vão
      // na hora. Feedback explícito senão o operador acha que não funcionou e re-clica.
      if (path === 'resend-nudge' || path === 'notify-expired') {
        setLeadInfo(d.alreadyQueued ? 'Já estava na fila.'
          : path === 'notify-expired' ? 'Aviso de prazo enfileirado — sai em instantes.'
          : 'Cobrança enfileirada — sai em instantes (3 por líder a cada 2 min).')
        setTimeout(() => setLeadInfo(''), 5000)
      }
      if (profile?.code) await loadLeaderLeads(profile.code)
    } catch (e) {
      setLeadError(e instanceof Error ? e.message : 'Erro')
    } finally {
      setLeadActionBusy(null)
    }
  }, [authenticatedFetch, profile?.code, loadLeaderLeads])

  const startEditing = () => {
    setEditForm({
      nome: profile?.name || '',
      email: profile?.email || '',
      celular: phone?.replace(/\D/g, '').replace(/@.*/, '') || '',
      endereco_cidade: profile?.location?.city || '',
      endereco_bairro: profile?.location?.neighborhood || '',
      endereco_cep: profile?.location?.zip || '',
    })
    setEditing(true)
  }

  const saveEdit = async () => {
    setSaving(true)
    try {
      const cleanPhone = phone?.replace(/\D/g, '').replace(/@.*/, '') || ''
      await authenticatedFetch('/api/crm/cadastro-cliente/upsert', {
        method: 'POST',
        body: JSON.stringify({
          phone: cleanPhone,
          nome: editForm.nome,
          email: editForm.email,
          endereco_cidade: editForm.endereco_cidade,
          endereco_bairro: editForm.endereco_bairro,
          endereco_cep: editForm.endereco_cep,
          // Sem isso o backend não atualiza whatsapp_conversations.remote_name —
          // o nome corrigido ficava só no cadastro e o pushname antigo persistia.
          conversation_id: conversationId,
        })
      })
      setEditing(false)
      // Reload profile
      if (cleanPhone.length >= 8) loadProfile(cleanPhone)
    } catch {}
    finally { setSaving(false) }
  }

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'
  const fmtDateTime = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'
  const fmtCpf = (c: string) => { const d = c.replace(/\D/g, ''); return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : c }
  const fmtCurrency = (v: number | null | undefined) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  const fmtVenc = (d: string | null) => { if (!d) return '-'; const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y.slice(2)}` }

  if (!phone) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground px-4">
        <FiUser className="w-10 h-10 mb-3 opacity-20" />
        <span className="text-sm font-medium">Perfil do Contato</span>
        <span className="text-[11px] text-center mt-1 opacity-60">Selecione uma conversa para ver o perfil completo</span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <FiLoader className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="text-[11px] text-muted-foreground mt-2">Carregando perfil...</span>
      </div>
    )
  }

  const p = profile
  const typeColor = TYPE_COLORS[p?.type || 'Contato'] || 'bg-gray-500'
  const typeLabel = TYPE_LABELS[p?.type || 'Contato'] || p?.type || 'Contato'
  // Prioridade: cadastro Oracle/MySQL > nome extraído pelo bot > pushname > fallback.
  // Quando NÃO há cadastro, o backend faz fallback de profile.name pra
  // remote_name (pushname WhatsApp) — que pode ser branding/apelido tipo
  // "EXCELÊNCIA", "CRBA", "Mhy❤️". Nesse caso o nome extraído pelo bot
  // (resposta direta a "qual seu nome completo?") é mais confiável.
  // Mesma prioridade que a inbox usa (WhatsAppInboxPanel:1959-1985).
  const isPlaceholderName = (s?: string | null) =>
    !s || ['Desconhecido', 'Sem nome', 'Não informado', 'Cliente', 'Lead'].includes(String(s).trim())
  const extractedNome = aiSession?.extractedData?.nome || aiSession?.extractedData?.name
  const registeredName = p?.isRegistered ? p?.name : null
  const pushName = !p?.isRegistered ? p?.name : null
  const displayName =
    (!isPlaceholderName(registeredName) && registeredName) ||
    (extractedNome && String(extractedNome).trim()) ||
    (!isPlaceholderName(pushName) && pushName) ||
    (!isPlaceholderName(contactName) && contactName) ||
    'Desconhecido'

  // Cabeçalho considera a pessoa ATIVA (troca de cadastro em linha
  // compartilhada): nome, tipo e cor dela; os chips Ativa/Inativa/Cadastrado
  // são do perfil canônico e somem durante a troca (não valem pra outra pessoa).
  const shownName = altCadastro?.nome || displayName
  const altCli = altCadastro ? financeiro?.cliente : null
  const shownTypeLabel = altCadastro ? (altCli?.tipoLabel || 'Consultora') : typeLabel
  const shownTypeColor = altCadastro ? (TYPE_COLORS[altCli?.tipoLabel || 'Consultora'] || 'bg-gray-500') : typeColor

  const campaignStatusIcon = (s: string) => {
    switch (s) { case 'read': return <FiEye className="w-3 h-3 text-violet-500" />; case 'delivered': return <FiCheck className="w-3 h-3 text-emerald-500" />; case 'sent': return <FiSend className="w-2.5 h-2.5 text-blue-500" />; case 'failed': return <FiX className="w-3 h-3 text-red-500" />; default: return <FiClock className="w-3 h-3 text-muted-foreground" /> }
  }

  return (
    /* Fundo transparente de propósito: o vidro vem do wrapper (.glass-panel na
       coluna lg+ e no drawer <lg do WhatsAppInboxPanel). */
    <div className={`h-full w-full flex flex-col overflow-hidden${neu ? ' neu-profile rounded-2xl' : ''}`}>
      {/* Header com avatar e tipo */}
      <div className={`shrink-0 px-4 py-4${neu ? '' : ' border-b border-border'}`}>
        <div className="flex items-center gap-3">
          {/* A foto que a pessoa ATIVA pôs no app.twms (pelo código do Draco — troca junto
              quando se escolhe outro cadastro da linha); sem ela, as iniciais na cor do tipo. Clica e amplia. */}
          <FotoPessoa codigo={effCode} nome={shownName} tamanho={48}
            classeIniciais={`font-bold ${neu ? 'neu-avatar text-foreground' : `${shownTypeColor} text-white`}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">{shownName.toUpperCase()}</div>
            <div className="text-[10px] text-muted-foreground">{(() => {
              const d = (phone || '').replace(/\D/g, '').replace(/@.*/, '')
              if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`
              if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`
              if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
              if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`
              return d
            })()}</div>
          </div>
          <button onClick={editing ? saveEdit : startEditing} disabled={saving}
            className={`p-1.5 rounded-lg transition-colors shrink-0 ${editing ? 'bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25' : 'text-muted-foreground/40 hover:text-foreground hover:bg-secondary'}`}
            title={editing ? 'Salvar' : 'Editar contato'}>
            {saving ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : editing ? <FiSave className="w-3.5 h-3.5" /> : <FiEdit2 className="w-3.5 h-3.5" />}
          </button>
        </div>
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {altCadastro && (
            <button onClick={() => selectCadastro(altCadastro.codigo, altCadastro.nome, false)}
              title={`Você está vendo o cadastro #${altCadastro.codigo} (não o titular do número). Clique pra voltar ao cadastro original.`}
              className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-500/40 inline-flex items-center gap-1">
              <FiRepeat className="w-2.5 h-2.5" /> vendo #{altCadastro.codigo} · voltar
            </button>
          )}
          <span className={`text-[9px] px-2 py-0.5 rounded-full text-white font-medium ${shownTypeColor}`}>
            {shownTypeLabel}
          </span>
          {(altCadastro || p?.isRegisteredInOracle) && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium">
              DRACO
            </span>
          )}
          {!altCadastro && p?.isRegistered && !p?.isRegisteredInOracle && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium">
              Cadastrado
            </span>
          )}
          {!altCadastro && !p?.isRegistered && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
              Não cadastrado
            </span>
          )}
          {!altCadastro && p?.isActive && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium">
              Ativa {p.activeWeek}
            </span>
          )}
          {!altCadastro && p?.isRegistered && !p?.isActive && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 font-medium">
              Inativa
            </span>
          )}
        </div>

        {/* Aviso de número compartilhado entre múltiplos cadastros. Padrão típico:
            1 dono real (DONA, celular) + 0-2 "co-shares" reais (celular/tel.1 de
            outras pessoas — família, sócio, mesma linha) + N consultoras com o
            número como tel.2 (rede da líder/empresário — ruído, alto volume).
            UI prioriza DONA + co-shares; tel.2 colapsa pra não enterrar o sinal. */}
        {phoneCadastros?.shared && phoneCadastros.matches.length > 1 && (() => {
          const typeLbl = (t: string) =>
            t === '1' ? 'Empresária' : t === '2' ? 'Líder' : t === '3' ? 'Consultora' : `tipo ${t}`;
          const fieldLbl = (f: string) =>
            f === 'celular' ? 'celular' : f === 'telefone1' ? 'tel. 1' : 'tel. 2';
          const all = phoneCadastros.matches;
          const dona = all.find(m => m.is_canonical) || null;
          const primary = all.filter(m => !m.is_canonical && m.source_field !== 'telefone2');
          const tel2 = all.filter(m => !m.is_canonical && m.source_field === 'telefone2');
          const summaryBits: string[] = [];
          const cCel = primary.filter(m => m.source_field === 'celular').length;
          const cTel1 = primary.filter(m => m.source_field === 'telefone1').length;
          if (cCel) summaryBits.push(`${cCel} celular`);
          if (cTel1) summaryBits.push(`${cTel1} tel. 1`);
          if (tel2.length) summaryBits.push(`${tel2.length} tel. 2`);
          return (
            <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[10px]">
              <div
                className="font-semibold text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1"
                title={`Mesmo número aparece em ${all.length} cadastros do Oracle DRACO. Ordem: DONA (canônico) > celular > tel. 1 > tel. 2.`}
              >
                📞 {all.length} cadastros · {summaryBits.join(' + ')}
              </div>

              {/* Linhas CLICÁVEIS (pedido do diretor 2026-07-20): selecionar um
                  cadastro troca a pessoa ativa — INFO, FINANCEIRO e Boleto
                  Asaas passam a mostrar os dados DELA (caso Kemily×Lucimar:
                  negociação com quem não é a dona canônica do número). */}
              {/* "É o perfil atual?" compara com o CÓDIGO do perfil carregado —
                  NÃO com a flag DONA: o canônico do telefone (DONA) e o perfil
                  podem divergir (caso real Kemily=DONA × perfil=Lucimar). */}
              {(() => {
                const profCode = p?.code ? String(p.code) : null
                const isViewing = (codigo: string) => String(effCode || '') === String(codigo)
                const row = (m: typeof all[number], isDona: boolean) => (
                  <button type="button" key={m.codigo}
                    onClick={() => selectCadastro(m.codigo, m.nome, String(m.codigo) === profCode)}
                    title={isViewing(m.codigo)
                      ? `Você está vendo os dados de ${m.nome}`
                      : `Ver os dados de ${m.nome} (cadastro, financeiro, boleto) — a conversa continua a mesma`}
                    className={`w-full text-left rounded px-1 -mx-1 transition-colors ${isViewing(m.codigo) ? 'bg-amber-500/20 text-foreground' : 'text-muted-foreground hover:bg-amber-500/15'}`}>
                    <span className={isDona ? 'font-semibold text-foreground' : ''}>{m.nome}</span>
                    <span className="opacity-60">
                      {' · '}{typeLbl(m.tipo_consultor)}{' · '}{fieldLbl(m.source_field)}
                      {isDona && <>{' · '}<span className="font-medium text-amber-600 dark:text-amber-400">DONA</span></>}
                    </span>
                    {isViewing(m.codigo) && <span className="ml-1 text-[9px] font-bold text-amber-700 dark:text-amber-300">◂ vendo</span>}
                  </button>
                )
                return (
                  <>
                    {dona && row(dona, true)}
                    {primary.length > 0 && (
                      <ul className="mt-0.5 space-y-0.5">
                        {primary.map(m => <li key={m.codigo}>{row(m, false)}</li>)}
                      </ul>
                    )}
                  </>
                )
              })()}

              {tel2.length > 0 && (
                <div className="mt-1 pt-1 border-t border-amber-500/20">
                  <button
                    type="button"
                    onClick={() => setSharedTel2Expanded(v => !v)}
                    className="w-full text-left text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                    title="Cadastros que registraram este número como telefone secundário (tel. 2). Normalmente é a rede da líder/empresário — clique pra ver."
                  >
                    {sharedTel2Expanded ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
                    <span>{tel2.length} como tel. 2</span>
                    <span className="opacity-50">(rede)</span>
                  </button>
                  {sharedTel2Expanded && (
                    <p className="mt-1 ml-4 leading-relaxed text-muted-foreground">
                      {tel2.map(m => m.nome).join(' · ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Seções expandíveis */}
      <div className={`flex-1 overflow-y-auto${neu ? ' px-2 pt-2 pb-3 rounded-b-2xl' : ''}`}>
        {/* ─── VALOR NA ASSESSORIA (cob.twms) — no TOPO, só para ver (pedido do diretor,
            13/10 e 16/10/2026). Número calculado pelo próprio cob; não vai para boleto. */}
        {effCode && <HonorariosCob authenticatedFetch={authenticatedFetch} codigo={effCode} />}
        {/* ─── INFO ─── */}
        <button onClick={() => toggleSection('info')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
          {expandedSections.has('info') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
          <FiUser className="w-3 h-3" /> INFORMAÇÕES
        </button>
        {expandedSections.has('info') && (
          <div className="px-4 py-2 space-y-1.5 border-b border-border/50">
            {/* Modo edição */}
            {editing ? (
              <div className="space-y-2">
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Nome</label>
                  <input value={editForm.nome} onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Email</label>
                  <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Cidade</label>
                  <input value={editForm.endereco_cidade} onChange={e => setEditForm(f => ({ ...f, endereco_cidade: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Bairro</label>
                    <input value={editForm.endereco_bairro} onChange={e => setEditForm(f => ({ ...f, endereco_bairro: e.target.value }))}
                      className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                  </div>
                  <div className="w-24">
                    <label className="text-[9px] text-muted-foreground uppercase tracking-wider">CEP</label>
                    <input value={editForm.endereco_cep} onChange={e => setEditForm(f => ({ ...f, endereco_cep: e.target.value }))}
                      className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                  </div>
                </div>
                <button onClick={() => setEditing(false)}
                  className="w-full text-[10px] text-muted-foreground hover:text-foreground py-1 transition-colors">
                  Cancelar
                </button>
              </div>
            ) : (
            <>
            {/* Pessoa ATIVA trocada (linha compartilhada): campos vêm do
                dossier DRACO dela, não do perfil canônico do telefone. */}
            {altCadastro && (() => {
              const c = financeiro?.cliente
              if (!c) return (
                <div className="flex items-center gap-2 py-1 text-[11px] text-muted-foreground">
                  <FiLoader className="w-3 h-3 animate-spin" /> Carregando cadastro #{altCadastro.codigo}…
                </div>
              )
              return (
                <>
                  <div className="flex items-center gap-2 text-[11px]">
                    <FiHash className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground">Código:</span>
                    <span className="text-foreground font-medium">{c.codigo}</span>
                  </div>
                  {c.cpf && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <FiCreditCard className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">CPF:</span>
                      <span className="text-foreground font-medium">{fmtCpf(c.cpf)}</span>
                    </div>
                  )}
                  {c.email && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <FiMail className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-foreground truncate">{c.email}</span>
                    </div>
                  )}
                  {c.grupo && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <FiGrid className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Grupo:</span>
                      <span className="text-foreground font-medium truncate">{c.grupo}</span>
                    </div>
                  )}
                  {c.distrito && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <FiTarget className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Distrito:</span>
                      <span className="text-foreground font-medium truncate">{c.distrito}</span>
                    </div>
                  )}
                  {c.cidade && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <FiMapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-foreground truncate">{c.cidade}{c.cep ? ` - ${c.cep}` : ''}</span>
                    </div>
                  )}
                </>
              )
            })()}
            {!altCadastro && (
            <>
            {p?.code && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiHash className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Código:</span>
                <span className="text-foreground font-medium">{p.code}</span>
              </div>
            )}
            {p?.cpf && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiCreditCard className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">CPF:</span>
                <span className="text-foreground font-medium">{fmtCpf(p.cpf)}</span>
              </div>
            )}
            {p?.email && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiMail className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-foreground truncate">{p.email}</span>
              </div>
            )}
            {p?.hierarchy && (
              <>
                <div className="flex items-center gap-2 text-[11px]">
                  <FiGrid className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Grupo:</span>
                  <span className="text-foreground font-medium truncate">{p.hierarchy.group}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <FiTarget className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Distrito:</span>
                  <span className="text-foreground font-medium truncate">{p.hierarchy.district}</span>
                </div>
              </>
            )}
            {p?.location?.city && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiMapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-foreground truncate">{p.location.city}{p.location.zip ? ` - ${p.location.zip}` : ''}</span>
              </div>
            )}
            </>
            )}
            {/* Telefone adicional anotado na conversa (pedido do diretor, 16/10/2026) */}
            <TelefonesExtra phone={phone} codigo={effCode} authenticatedFetch={authenticatedFetch} />
            {/* Mensagens stats */}
            {p?.messages && p.messages.total > 0 && (
              <div className="flex items-center gap-2 text-[11px] pt-1">
                <FiMessageSquare className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">{p.messages.total} msgs</span>
                <span className="text-[9px] text-muted-foreground/60">({p.messages.fromContact} recebidas, {p.messages.fromUs} enviadas)</span>
              </div>
            )}
            {p?.messages?.firstMessage && (
              <div className="text-[10px] text-muted-foreground/60 pl-5">
                Primeiro contato: {fmtDate(p.messages.firstMessage)}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Seção CRM removida em 2026-05-15 junto com o painel CRM.
            Os dados em p.crm continuam sendo populados pelo backend (vêm de
            bot_conversation_state via JOIN) — preservados para o bot Juliane
            e possível UI futura. */}

        {/* ─── ATENDIMENTO COM IA ─── (desligada no fin — SHOW_CRM_SECTIONS) */}
        {SHOW_CRM_SECTIONS && aiSession && (
          <>
            <button onClick={() => toggleSection('ai')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('ai') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <span className={`w-2 h-2 rounded-full ${aiSession.botActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              ATENDIMENTO COM IA
              {aiSession.currentStage && (
                <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `color-mix(in srgb, ${aiSession.currentStage.color} 15%, transparent)`, color: aiSession.currentStage.color }}>
                  {aiSession.currentStage.title}
                </span>
              )}
            </button>
            {expandedSections.has('ai') && (
              <div className="px-4 py-3 space-y-2 border-b border-border/50">
                {/* Marcos do lead — substitui "Status: Bot atendendo" (já no badge do header).
                    4 eventos: entrada, dados completos, enviado pra líder, convertido.
                    Cada um com ✓ + data quando ocorreu, ◯ + "pendente" quando não. */}
                {aiSession.timeline && (
                  <div className="space-y-1">
                    {([
                      { key: 'entrada',         label: 'Data de entrada',  at: aiSession.timeline.entrada?.at,         sub: null },
                      { key: 'dadosCompletos',  label: 'Dados completos',  at: aiSession.timeline.dadosCompletos?.at,  sub: null },
                      { key: 'enviadoLider',    label: 'Enviado para líder', at: aiSession.timeline.enviadoLider?.at ?? null,
                        sub: aiSession.timeline.enviadoLider?.leaderName ? `${aiSession.timeline.enviadoLider.leaderName}${aiSession.timeline.enviadoLider.leaderCode ? ` (${aiSession.timeline.enviadoLider.leaderCode})` : ''}` : null },
                      { key: 'convertido',      label: 'Convertido',       at: aiSession.timeline.convertido?.at ?? null,
                        sub: aiSession.timeline.convertido?.nome ? `${aiSession.timeline.convertido.nome}${aiSession.timeline.convertido.codigo ? ` (${aiSession.timeline.convertido.codigo})` : ''}` : null },
                    ] as const).map((evt) => {
                      const done = !!evt.at
                      // convertido sem timestamp mas com cadastro: mostra ✓ sem data (Oracle ainda não preencheu converted_consultant_at)
                      const partial = !done && evt.key === 'convertido' && !!aiSession.timeline?.convertido
                      return (
                        <div key={evt.key} className="flex items-start gap-2 text-[11px]">
                          <span className={`mt-[3px] inline-flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0 ${
                            done ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                            partial ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                            'bg-muted text-muted-foreground/60'
                          }`}>
                            {done || partial ? (
                              <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none">
                                <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={done || partial ? 'text-foreground font-medium' : 'text-muted-foreground'}>{evt.label}</span>
                              <span className={`tabular-nums shrink-0 text-[10px] ${
                                done ? 'text-muted-foreground' : partial ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/60'
                              }`}>
                                {done && evt.at
                                  ? new Date(evt.at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                  : partial ? 'sem data' : 'pendente'}
                              </span>
                            </div>
                            {evt.sub && (
                              <div className="text-[10px] text-muted-foreground truncate">{evt.sub}</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {aiSession.intent && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Origem:</span>
                    <span className="font-medium text-foreground truncate ml-2" title={aiSession.intent}>
                      {formatIntent(aiSession.intent)}
                    </span>
                  </div>
                )}
                {aiSession.temperature && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Temperatura:</span>
                    <span className="font-medium text-foreground">{formatTemperature(aiSession.temperature)}</span>
                  </div>
                )}

                {/* Dados coletados pelo bot — metadata interna (updated_at,
                    created_at, version, _*) é filtrada porque o backend grava
                    `updated_at: new Date().toISOString()` dentro do JSON
                    lead_extracted_data (server.cjs:11257). Labels/valores
                    humanizados (CPF, CEP, telefone, ISO datetime, arrays).
                    Dedup por label canônico — bot às vezes grava `nome` E
                    `name` (ou `cidade` E `city`); o primeiro com valor real
                    vence pra não duplicar linha "Nome:". Linhas com valor
                    vazio são omitidas. */}
                {(() => {
                  const groups: Record<string, { key: string; label: string; value: string }> = {}
                  for (const [k, v] of Object.entries(aiSession.extractedData || {})) {
                    if (EXTRACTED_META_KEYS.has(k.toLowerCase()) || k.startsWith('_')) continue
                    const value = formatExtractedValue(k, v)
                    if (!value) continue
                    const label = formatExtractedLabel(k)
                    if (!groups[label]) groups[label] = { key: k, label, value }
                  }
                  const entries = Object.values(groups)
                  if (entries.length === 0) return null
                  return (
                    <div className="pt-2 border-t border-border/40">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">Dados coletados</div>
                      <div className="space-y-0.5">
                        {entries.map(({ key, label, value }) => (
                          <div key={key} className="flex items-start gap-2 text-[10px]">
                            <span className="text-muted-foreground shrink-0">{label}:</span>
                            <span className="text-foreground break-words flex-1 min-w-0">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
          </>
        )}

        {/* ─── CAMPANHAS ─── (desligada no fin — SHOW_CRM_SECTIONS) */}
        {SHOW_CRM_SECTIONS && (<>
        <button onClick={() => toggleSection('campaigns')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
          {expandedSections.has('campaigns') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
          <FiSend className="w-3 h-3" /> CAMPANHAS
          {(p?.campaigns?.length || 0) > 0 && <span className="ml-auto text-[9px] bg-blue-500/15 text-blue-500 px-1.5 py-0.5 rounded-full">{p!.campaigns.length}</span>}
        </button>
        {expandedSections.has('campaigns') && (
          <div className="px-4 py-2 space-y-1.5 border-b border-border/50">
            {(p?.campaigns?.length || 0) === 0 ? (
              <div className="text-[11px] text-muted-foreground/60 text-center py-2">
                Nenhuma campanha enviada
              </div>
            ) : (
              p!.campaigns.map((c, i) => {
                const st = c.status
                const stColor = st === 'read' ? 'text-violet-500' : st === 'delivered' ? 'text-emerald-500' : st === 'sent' ? 'text-blue-500' : st === 'failed' ? 'text-red-500' : 'text-muted-foreground'
                const stLabel = st === 'read' ? 'Leu' : st === 'delivered' ? 'Recebeu' : st === 'sent' ? 'Enviado' : st === 'failed' ? 'Falhou' : 'Pendente'
                const stBg = st === 'read' ? 'bg-violet-500/10' : st === 'delivered' ? 'bg-emerald-500/10' : st === 'sent' ? 'bg-blue-500/10' : st === 'failed' ? 'bg-red-500/10' : 'bg-muted/30'
                const fmtFull = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null
                const tipSent = c.sentAt ? `Enviado em ${fmtFull(c.sentAt)}` : 'Aguardando envio'
                const tipDelivered = c.deliveredAt ? `Entregue em ${fmtFull(c.deliveredAt)}` : 'Aguardando entrega'
                const tipRead = c.readAt ? `Lido em ${fmtFull(c.readAt)}` : 'Não lido ainda'
                return (
                  <div key={i} className={`p-2 rounded-lg ${stBg} border border-border/30`}>
                    <div className="flex items-center gap-1.5">
                      {campaignStatusIcon(st)}
                      <span className="text-[10px] font-medium text-foreground truncate flex-1">{c.templateName}</span>
                      {c.error && <FiAlertCircle className="w-3 h-3 text-red-400 shrink-0" title={c.error} />}
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-5">
                      <span className={`text-[9px] font-medium ${stColor}`}>{stLabel}</span>
                      <span className="text-[9px] text-muted-foreground/50">•</span>
                      <span className="text-[9px] text-muted-foreground">{fmtDateTime(c.sentAt || c.date)}</span>
                    </div>
                    {/* Timeline de entrega */}
                    <div className="flex items-center gap-1 mt-1.5 ml-5">
                      <div className={`w-1.5 h-1.5 rounded-full ${c.sentAt ? 'bg-blue-500' : 'bg-muted-foreground/20'}`} title={tipSent} />
                      <div className={`w-4 h-px ${c.deliveredAt ? 'bg-emerald-500' : 'bg-muted-foreground/20'}`} />
                      <div className={`w-1.5 h-1.5 rounded-full ${c.deliveredAt ? 'bg-emerald-500' : 'bg-muted-foreground/20'}`} title={tipDelivered} />
                      <div className={`w-4 h-px ${c.readAt ? 'bg-violet-500' : 'bg-muted-foreground/20'}`} />
                      <div className={`w-1.5 h-1.5 rounded-full ${c.readAt ? 'bg-violet-500' : 'bg-muted-foreground/20'}`} title={tipRead} />
                      <span className="text-[8px] text-muted-foreground/50 ml-1">
                        {c.readAt ? fmtDateTime(c.readAt) : c.deliveredAt ? fmtDateTime(c.deliveredAt) : ''}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}
        </>)}

        {/* ─── COBRANÇA ─── (paralelo a CAMPANHAS; só aparece se houver
             disparo via device Faturamento OU estágio no funil de cobrança).
             Cor amber pra distinguir alerta financeiro de campanha de marketing. */}
        {((p?.cobrancas?.length || 0) > 0 || p?.cobrancaStage) && (
          <>
            <button onClick={() => toggleSection('cobranca')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('cobranca') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <LuBanknote className="w-3 h-3 text-amber-500" /> COBRANÇA
              {p?.cobrancaStage && (
                <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                      style={{
                        background: p.cobrancaStage.color ? `color-mix(in srgb, ${p.cobrancaStage.color} 15%, transparent)` : 'rgb(245 158 11 / 0.15)',
                        color: p.cobrancaStage.color || 'rgb(245 158 11)'
                      }}>
                  {p.cobrancaStage.title}
                </span>
              )}
              {!p?.cobrancaStage && (p?.cobrancas?.length || 0) > 0 && (
                <span className="ml-auto text-[9px] bg-amber-500/15 text-amber-500 px-1.5 py-0.5 rounded-full">{p!.cobrancas!.length}</span>
              )}
            </button>
            {expandedSections.has('cobranca') && (
              <div className="px-4 py-2 space-y-1.5 border-b border-border/50">
                {(p?.cobrancas?.length || 0) === 0 ? (
                  <div className="text-[11px] text-muted-foreground/60 text-center py-2">
                    {p?.cobrancaStage
                      ? `No funil de Cobrança em "${p.cobrancaStage.title}" — sem disparo registrado ainda`
                      : 'Nenhuma cobrança enviada'}
                  </div>
                ) : (
                  p!.cobrancas!.map((c, i) => {
                    const st = c.status
                    // Visual minimalista: única cor de fundo (muted), status fica
                    // no símbolo (✓/✓✓/⚠) + label discreta. Sem caixas coloridas.
                    const statusIcon = st === 'read' ? '✓✓' : st === 'delivered' ? '✓✓' : st === 'sent' ? '✓' : st === 'failed' ? '⚠' : '·'
                    const statusLabel = st === 'read' ? 'lida' : st === 'delivered' ? 'entregue' : st === 'sent' ? 'enviada' : st === 'failed' ? 'falhou' : 'pendente'
                    // Cor só na resolução final — entregue/lida = success neutro, falhou = warning amber.
                    const statusColor = st === 'read' ? 'text-emerald-500' : st === 'delivered' ? 'text-emerald-500/70' : st === 'failed' ? 'text-amber-500' : 'text-muted-foreground'
                    const fmtFull = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null
                    const tip = c.error
                      ? `${statusLabel} — ${c.error}`
                      : st === 'read' && c.readAt ? `Lida em ${fmtFull(c.readAt)}`
                      : st === 'delivered' && c.deliveredAt ? `Entregue em ${fmtFull(c.deliveredAt)}`
                      : c.sentAt ? `Enviada em ${fmtFull(c.sentAt)}`
                      : statusLabel
                    return (
                      <div key={i} className="flex items-baseline gap-2 py-1 border-b border-border/30 last:border-b-0">
                        <span className="text-[10px] text-foreground flex-1 truncate" title={c.templateName}>
                          {c.templateName}
                        </span>
                        <span className={`text-[9px] font-medium ${statusColor} tabular-nums`} title={tip}>
                          {statusIcon} {statusLabel}
                        </span>
                        <span className="text-[9px] text-muted-foreground/60 tabular-nums">
                          {fmtDateTime(c.sentAt || c.date)}
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </>
        )}

        {/* ─── FINANCEIRO ─── (débitos em aberto + pagamentos, do Oracle).
             Lazy: só busca ao expandir. Só aparece pra cliente Oracle. Read-only —
             reenviar cobrança continua no painel de Cobrança. */}
        {!!effCode && (altCadastro || (p?.code && (p.isRegisteredInOracle || p.dataSource === 'oracle'))) && (
          <>
            <button onClick={() => toggleSection('financeiro')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('financeiro') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <LuBanknote className="w-3 h-3 text-emerald-500" /> FINANCEIRO{altCadastro ? ` · #${altCadastro.codigo}` : ''}
              {financeiro && (financeiro.totalAberto || 0) > 0 && (
                <span className="ml-auto text-[9px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded-full tabular-nums">{fmtCurrency(financeiro.totalAberto)}</span>
              )}
            </button>
            {expandedSections.has('financeiro') && (
              <div className="px-4 py-2 space-y-2 border-b border-border/50">
                {financeiroLoading && (
                  <div className="flex items-center justify-center gap-2 py-3 text-[11px] text-muted-foreground">
                    <FiLoader className="w-3.5 h-3.5 animate-spin" /> Carregando financeiro…
                  </div>
                )}
                {financeiroError && !financeiroLoading && (
                  <div className="text-[11px] text-amber-600 dark:text-amber-400 py-2 flex items-center gap-2">
                    <FiAlertCircle className="w-3 h-3 shrink-0" />
                    <span className="flex-1">{financeiroError}</span>
                    <button onClick={() => { financeiroReqRef.current = null; if (effCode) loadFinanceiro(effCode) }} className="text-primary hover:underline shrink-0">Tentar de novo</button>
                  </div>
                )}
                {financeiro && !financeiroLoading && (() => {
                  const abertos = financeiro.boletosAbertos || []
                  const pagos = financeiro.pagamentos || []
                  return (
                    <>
                      {/* Resumo */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-md bg-red-500/5 border border-red-500/20 px-2 py-1.5">
                          <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Em aberto</div>
                          <div className="text-[12px] font-bold text-red-600 dark:text-red-400 tabular-nums">{fmtCurrency(financeiro.totalAberto)}</div>
                        </div>
                        <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 px-2 py-1.5">
                          <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Pago (recente)</div>
                          <div className="text-[12px] font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtCurrency(financeiro.pagamentosTotal)}</div>
                        </div>
                      </div>

                      {/* Débitos em aberto */}
                      <div>
                        <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Débitos em aberto ({abertos.length})</div>
                        {abertos.length === 0 ? (
                          <div className="text-[10px] text-muted-foreground/60 italic py-0.5">Nenhum débito em aberto.</div>
                        ) : (
                          <div className="space-y-1">
                            {abertos.map(b => {
                              const venceu = b.diasAtraso > 0
                              const atrasoColor = !venceu ? 'text-amber-500' : b.diasAtraso <= 7 ? 'text-amber-500' : b.diasAtraso <= 30 ? 'text-orange-500' : 'text-red-500'
                              return (
                                <div key={`${b.documento}-${b.serie}`} className="px-2 py-1 rounded-md border border-border bg-card text-[10px]">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono text-muted-foreground">{b.documento}{b.serie ? `·${b.serie}` : ''}</span>
                                    <span className={`font-bold tabular-nums ${atrasoColor}`}>{venceu ? `${b.diasAtraso}d` : 'a vencer'}</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-2 mt-0.5">
                                    <span className="text-muted-foreground/70">venc {fmtVenc(b.vencimento)}</span>
                                    <span className="font-semibold text-red-500 tabular-nums">{fmtCurrency(b.saldo)}</span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>

                      {/* Pagamentos recentes */}
                      {pagos.length > 0 && (
                        <div>
                          <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Pagamentos recentes ({pagos.length})</div>
                          <div className="space-y-1">
                            {pagos.map(pg => (
                              <div key={`${pg.documento}-${pg.serie}`} className="px-2 py-1 rounded-md border border-border bg-card text-[10px]">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-mono text-muted-foreground">{pg.documento}{pg.serie ? `·${pg.serie}` : ''}</span>
                                  <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-medium ${pg.quitado ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>{pg.quitado ? 'Quitado' : 'Parcial'}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-0.5">
                                  <span className="text-muted-foreground/70">pago {fmtVenc(pg.ultPagamento)}</span>
                                  <span className="font-semibold text-emerald-600 tabular-nums">{fmtCurrency(pg.valorPago)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </>
        )}

        {/* ─── BOLETO ASAAS ─── (só existe nesta cópia do fin; admin-only — o
             endpoint do crm exige admin). Gera cobrança real no Asaas pro
             cliente da conversa; o card resultante é arrastável pro chat
             (envia o PDF como documento) ou enviável pelo botão. */}
        {user?.isAdmin && (
          <>
            <button onClick={() => toggleSection('boleto')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('boleto') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <FiFileText className="w-3 h-3 text-violet-500" /> BOLETO ASAAS
              {gerados.length > 0 && <span className="ml-auto text-[9px] bg-violet-500/15 text-violet-500 px-1.5 py-0.5 rounded-full tabular-nums">{gerados.length === 1 ? fmtCurrency(gerados[0].valorTotal || gerados[0].valor) : `${gerados.length} boletos`}</span>}
            </button>
            {expandedSections.has('boleto') && (
              <div className="px-4 py-2 space-y-2 border-b border-border/50">
                {boletoError && (
                  <div className="text-[11px] text-red-500 flex items-start gap-1.5">
                    <FiAlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> <span>{boletoError}</span>
                  </div>
                )}

                {/* ── Saúde da dívida (DRACO) + valor atualizado com multa/juros
                    parametrizáveis (charge_config — Cadastros → Contas e
                    Cobrança). É o número certo pra negociar/gerar o boleto. */}
                {(boletoAlvo?.codigo || effCode) && (() => {
                  const divCode = boletoAlvo?.codigo || effCode
                  // spinner SEMPRE que recarregando — mostrar o financeiro
                  // anterior com o rótulo do novo código seria dado errado
                  if (financeiroLoading) return (
                    <div className="flex items-center gap-2 py-1 text-[10px] text-muted-foreground">
                      <FiLoader className="w-3 h-3 animate-spin" /> Consultando dívida no DRACO…
                    </div>
                  )
                  const abertos = financeiro?.boletosAbertos || []
                  if (!financeiro) return null
                  const multa = chargeCfg?.multaPct ?? 0
                  const jurosDia = chargeCfg?.jurosDia ?? 0
                  const atualiza = (saldo: number, dias: number) =>
                    dias > 0 ? saldo * (1 + multa / 100 + (jurosDia / 100) * dias) : saldo
                  const semHonorarios = abertos.reduce((s, b) => s + atualiza(b.saldo, b.diasAtraso), 0)
                  // 16/10/2026: honorários NÃO entram mais aqui (o diretor negocia sem
                  // custas advocatícias). O valor da assessoria só aparece, para consulta,
                  // no bloco "Valor na assessoria (cob)" do topo da lateral.
                  const totalAtualizado = semHonorarios
                  const totalOriginal = financeiro.totalAberto || 0
                  return (
                    <div className="rounded-md border border-border bg-card px-2 py-1.5 space-y-1">
                      <div className="flex items-center justify-between text-[9px] uppercase tracking-wider">
                        <span className="font-semibold text-muted-foreground truncate">
                          Dívida no DRACO · #{divCode}{boletoAlvo ? ` · ${boletoAlvo.nome.split(' ')[0]}` : ''}
                        </span>
                        <span className="text-muted-foreground shrink-0">{abertos.length} título(s)</span>
                      </div>
                      {abertos.length === 0 ? (
                        <div className="text-[10px] text-emerald-600 dark:text-emerald-400">Nada em aberto — cliente em dia. ✓</div>
                      ) : (
                        <>
                          {abertos.map(b => {
                            const at = atualiza(b.saldo, b.diasAtraso)
                            return (
                              <div key={`${b.documento}-${b.serie}`} className="flex items-center gap-2 text-[10px]"
                                title={`Original ${fmtCurrency(b.saldo)} · venc ${fmtVenc(b.vencimento)}${b.diasAtraso > 0 ? ` · ${b.diasAtraso} dias de atraso` : ''}`}>
                                <span className="font-mono text-muted-foreground shrink-0">{b.documento}</span>
                                <span className={`shrink-0 ${b.diasAtraso > 0 ? 'text-red-500' : 'text-amber-500'}`}>
                                  {b.diasAtraso > 0 ? `${b.diasAtraso}d` : 'a vencer'}
                                </span>
                                <span className="flex-1" />
                                <span className="text-muted-foreground/70 tabular-nums line-through">{fmtCurrency(b.saldo)}</span>
                                <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">{fmtCurrency(at)}</span>
                              </div>
                            )
                          })}
                          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-border/50">
                            <span className="text-muted-foreground">Original {fmtCurrency(totalOriginal)}</span>
                            <span className="font-bold text-red-600 dark:text-red-400 tabular-nums">Atualizado {fmtCurrency(semHonorarios)}</span>
                          </div>
                          {/* ── Tabela de parcelamento do valor ATUALIZADO (pedido do diretor
                              2026-08-20): a negociação acontece na conversa, então ele precisa
                              colar "4x de R$ 142,75" sem calcular na mão. Divisão simples, SEM
                              juros de parcelamento — é o mesmo número que o Asaas cobra ao gerar
                              o boleto parcelado (installmentCount + totalValue). A parcela
                              arredonda pra CIMA no centavo pra nunca cotar menos que a dívida;
                              o rateio exato do último centavo quem faz é o Asaas. */}
                          {(() => {
                            // O Asaas recusa parcela abaixo de R$ 5,00 — não ofertar plano que
                            // não dá pra gerar depois. Dívida pequena simplesmente mostra menos
                            // linhas (e some de vez quando nem 2× cabe).
                            // Arredonda o total pro centavo ANTES de dividir: sem isso o 1×
                            // sai um centavo acima do total logo acima dele (508,75 × 310d =
                            // 570,9716… → "Atualizado 570,97" mas "1× 570,98"), porque o ceil
                            // pega a sujeira do float.
                            const totalCents = Math.round(totalAtualizado * 100) / 100
                            const maxP = Math.min(6, Math.floor(totalCents / 5))
                            if (maxP < 2) return null
                            const planos = Array.from({ length: maxP }, (_, i) => {
                              const n = i + 1
                              return { n, parcela: Math.ceil((totalCents / n) * 100) / 100 }
                            })
                            // "x" e não "×" no texto copiado: vai pro WhatsApp, onde o ASCII
                            // sobrevive a qualquer teclado/fonte do outro lado.
                            const linhaTxt = (p: { n: number; parcela: number }) =>
                              p.n === 1 ? `À vista: ${fmtCurrency(p.parcela)}` : `${p.n}x de ${fmtCurrency(p.parcela)}`
                            const tabelaTxt = [`Valor atualizado: ${fmtCurrency(totalCents)}`, ...planos.map(linhaTxt)].join('\n')
                            return (
                              <div className="pt-1 border-t border-border/50 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Parcelamento{maxP < 6 ? ` até ${maxP}×` : ' até 6×'}
                                  </span>
                                  <span className="flex-1" />
                                  <button onClick={() => boletoCopy('plano', tabelaTxt)}
                                    title="Copiar a tabela inteira pra colar na conversa"
                                    className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                                    {boletoCopied === 'plano' ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />}
                                    {boletoCopied === 'plano' ? 'copiado' : 'copiar tabela'}
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2">
                                  {planos.map(p => (
                                    <button key={p.n} onClick={() => boletoCopy(`plano-${p.n}`, linhaTxt(p))}
                                      title={`Copiar "${linhaTxt(p)}"`}
                                      className="group flex items-center gap-1.5 rounded px-1 py-0.5 text-[10px] text-left hover:bg-secondary">
                                      <span className="text-muted-foreground shrink-0 w-4">{p.n}×</span>
                                      <span className="flex-1 tabular-nums font-medium text-foreground">{fmtCurrency(p.parcela)}</span>
                                      {boletoCopied === `plano-${p.n}`
                                        ? <FiCheck className="w-3 h-3 text-emerald-500 shrink-0" />
                                        : <FiCopy className="w-3 h-3 opacity-0 group-hover:opacity-100 text-muted-foreground shrink-0" />}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )
                          })()}
                          {gerados.length === 0 && (
                            <button onClick={() => setBoletoForm(fm => ({ ...fm, cliente: divCode!, valor: totalAtualizado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }))}
                              title={`Preenche o boleto com a dívida atualizada: multa ${multa}% + juros ${jurosDia}%/dia (≈ ${(jurosDia * 30).toFixed(1)}%/mês) sobre os dias de atraso. Parâmetros ajustáveis em Cadastros → Contas e Cobrança.`}
                              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-violet-500/40 text-violet-600 dark:text-violet-400 text-[10px] font-medium px-2 py-1 hover:bg-violet-500/10">
                              Usar valor atualizado no boleto
                            </button>
                          )}
                          <div className="text-[9px] text-muted-foreground/60">
                            multa {multa}% + juros {jurosDia}%/dia (≈{(jurosDia * 30).toFixed(1)}%/mês) · ajustar em Cadastros → Contas e Cobrança
                          </div>
                        </>
                      )}
                    </div>
                  )
                })()}
                {gerados.length === 0 ? (
                  <>
                    <div>
                      <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Cliente (código, CPF ou nome)</label>
                      <input value={boletoForm.cliente} onChange={e => setBoletoForm(f => ({ ...f, cliente: e.target.value }))}
                        placeholder="código, CPF ou nome — pode ser outra pessoa" title="Campo independente da conversa: digite o código DRACO/Tupperware, CPF ou nome de QUALQUER cliente — os dados e a dívida dele aparecem abaixo"
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                      {boletoAlvoLoading && (
                        <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <FiLoader className="w-3 h-3 animate-spin" /> Buscando cliente no DRACO…
                        </div>
                      )}
                      {!boletoAlvoLoading && boletoAlvo && (
                        <div className="mt-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-[10px]"
                          title="O boleto sai pra ESTA pessoa (não pro contato da conversa)">
                          <span className="font-semibold text-foreground">{boletoAlvo.nome}</span>
                          <span className="text-muted-foreground"> · #{boletoAlvo.codigo}{boletoAlvo.tipoLabel ? ` · ${boletoAlvo.tipoLabel}` : ''}{boletoAlvo.cidade ? ` · ${boletoAlvo.cidade}` : ''}</span>
                        </div>
                      )}
                      {!boletoAlvoLoading && boletoAlvoMatches.length > 1 && (
                        <div className="mt-1 rounded-md border border-border bg-card divide-y divide-border/50 max-h-32 overflow-y-auto scrollbar-thin">
                          {boletoAlvoMatches.map(m => (
                            <button key={m.codigo} onClick={() => setBoletoForm(f => ({ ...f, cliente: m.codigo }))}
                              title={`Selecionar ${m.nome} (#${m.codigo})`}
                              className="w-full text-left px-2 py-1 text-[10px] hover:bg-secondary">
                              <span className="text-foreground">{m.nome}</span>
                              <span className="text-muted-foreground"> · #{m.codigo}{m.tipoLabel ? ` · ${m.tipoLabel}` : ''}{m.cidade ? ` · ${m.cidade}` : ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {!boletoAlvoLoading && boletoAlvoVazio && (
                        <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">Nenhum cliente encontrado no DRACO com esse dado.</div>
                      )}
                    </div>
                    {/* ── NEGOCIAÇÃO (04/11): total do acordo, entrada opcional e parcelas
                        do restante. Rótulos em português de balcão — a operadora é leiga. */}
                    <div>
                      <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Valor total do acordo (R$)</label>
                      <input value={boletoForm.valor} onChange={e => { valorTocadoRef.current = true; setBoletoForm(f => ({ ...f, valor: e.target.value })) }}
                        placeholder="0,00" inputMode="decimal"
                        title="Quanto o cliente vai pagar no total. Já vem com o valor atualizado — pode mudar para dar desconto."
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Entrada (R$)</label>
                        <input value={boletoForm.entrada}
                          onChange={e => {
                            const v = e.target.value
                            setBoletoForm(f => {
                              // 1ª vez que coloca entrada: empurra a 1ª parcela pra 30 dias depois da entrada.
                              const venc = (!f.entrada.trim() && v.trim() && f.vencimento <= f.vencEntrada)
                                ? ymdLocal(new Date(new Date(f.vencEntrada + 'T12:00:00').getTime() + 30 * 86400000)) : f.vencimento
                              return { ...f, entrada: v, vencimento: venc }
                            })
                          }}
                          placeholder="sem entrada" inputMode="decimal"
                          title="Deixe vazio se não tiver entrada"
                          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                          {boletoForm.entrada.trim() ? 'Parcelas do restante' : 'Parcelas'}
                        </label>
                        <select value={boletoForm.parcelas} onChange={e => setBoletoForm(f => ({ ...f, parcelas: e.target.value }))}
                          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                          {PARCELAS_OPCOES.map(n => (
                            <option key={n} value={String(n)}>{n === 1 ? '1 (à vista)' : `${n}x`}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {boletoForm.entrada.trim() && (
                        <div>
                          <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Entrada vence</label>
                          <input type="date" value={boletoForm.vencEntrada} min={ymdLocal(new Date())}
                            onChange={e => setBoletoForm(f => ({ ...f, vencEntrada: e.target.value }))}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                        </div>
                      )}
                      <div className={boletoForm.entrada.trim() ? '' : 'col-span-2'}>
                        <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                          {Number(boletoForm.parcelas) > 1 ? '1ª parcela vence' : boletoForm.entrada.trim() ? 'Restante vence' : 'Vencimento'}
                        </label>
                        <input type="date" value={boletoForm.vencimento} min={ymdLocal(new Date())}
                          onChange={e => setBoletoForm(f => ({ ...f, vencimento: e.target.value }))}
                          title="No parcelado, as demais parcelas vencem uma por mês depois desta"
                          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                      </div>
                    </div>
                    {/* Resumo do que vai sair — é o que ela confere antes de gerar */}
                    {plano.erro ? (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400">{plano.erro}</div>
                    ) : plano.total ? (
                      <div className="rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 text-[11px] space-y-0.5">
                        <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Vai gerar</div>
                        {plano.entrada > 0 && (
                          <div className="flex justify-between gap-2"><span>Entrada</span>
                            <span className="tabular-nums font-semibold">{fmtCurrency(plano.entrada)} · {fmtVenc(boletoForm.vencEntrada)}</span></div>
                        )}
                        <div className="flex justify-between gap-2"><span>{plano.entrada > 0 ? 'Restante' : plano.n > 1 ? 'Parcelas' : 'Boleto'}</span>
                          <span className="tabular-nums font-semibold">
                            {plano.n > 1 ? `${plano.n}x de ${fmtCurrency(plano.parcela)}` : fmtCurrency(plano.restante)} · {plano.n > 1 ? `1ª ${fmtVenc(boletoForm.vencimento)}` : fmtVenc(boletoForm.vencimento)}
                          </span></div>
                        <div className="flex justify-between gap-2 pt-0.5 border-t border-border/50 text-muted-foreground"><span>Total</span>
                          <span className="tabular-nums">{fmtCurrency(plano.total)}</span></div>
                        {(() => {
                          const txt = [
                            `Valor do acordo: ${fmtCurrency(plano.total)}`,
                            ...(plano.entrada > 0 ? [`Entrada: ${fmtCurrency(plano.entrada)} (vence ${fmtVenc(boletoForm.vencEntrada)})`] : []),
                            plano.n > 1
                              ? `${plano.entrada > 0 ? 'Restante em ' : ''}${plano.n}x de ${fmtCurrency(plano.parcela)} (1ª vence ${fmtVenc(boletoForm.vencimento)})`
                              : `${plano.entrada > 0 ? 'Restante' : 'À vista'}: ${fmtCurrency(plano.restante)} (vence ${fmtVenc(boletoForm.vencimento)})`,
                          ].join('\n')
                          return (
                            <button onClick={() => boletoCopy('proposta', txt)} title="Copiar a proposta pra colar na conversa antes de gerar"
                              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                              {boletoCopied === 'proposta' ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />}
                              {boletoCopied === 'proposta' ? 'copiado' : 'copiar proposta'}
                            </button>
                          )
                        })()}
                      </div>
                    ) : null}
                    <div>
                      <label className="block text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Descrição (opcional)</label>
                      <input value={boletoForm.descricao} onChange={e => setBoletoForm(f => ({ ...f, descricao: e.target.value }))}
                        placeholder="aparece no boleto" title="Descrição impressa no boleto"
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                    </div>
                    <button onClick={gerarBoleto} disabled={boletoBusy || boletoAlvoLoading || boletoAlvoMatches.length > 1 || !boletoForm.cliente.trim() || !plano.total || !!plano.erro}
                      title={boletoAlvoMatches.length > 1 ? 'Escolha um cliente na lista acima antes de gerar' : 'Cria a cobrança no Asaas (boleto com QR pix embutido)'}
                      className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-[11px] font-medium px-2 py-1.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                      {boletoBusy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiFileText className="w-3 h-3" />}
                      {plano.entrada > 0 ? 'Gerar entrada + restante' : 'Gerar boleto'}
                    </button>
                  </>
                ) : (
                  <>
                    {gerados.map(boleto => (
                      <div key={boleto.paymentId} className="space-y-1.5">
                        <div draggable
                          onDragStart={e => {
                            e.dataTransfer.setData(ASAAS_BOLETO_MIME, JSON.stringify(boletoSendPayload(boleto)))
                            e.dataTransfer.effectAllowed = 'copy'
                          }}
                          title="Arraste este cartão pra área da conversa pra enviar o boleto (PDF)"
                          className="rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 cursor-grab active:cursor-grabbing select-none">
                          {boleto.rotulo && <div className="text-[9px] font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">{boleto.rotulo}</div>}
                          <div className="flex items-center justify-between gap-2 text-[11px]">
                            <span className="font-medium text-foreground truncate">{boleto.cliente.nome}</span>
                            <span className="text-[9px] text-muted-foreground shrink-0">#{boleto.cliente.codigo}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-0.5 text-[11px]">
                            <span className="font-bold text-violet-600 dark:text-violet-400 tabular-nums">
                              {boleto.installmentId ? `${boleto.parcelas}× de ${fmtCurrency(boleto.valorParcela)}` : fmtCurrency(boleto.valor)}
                            </span>
                            <span className="text-muted-foreground">{boleto.installmentId ? `1ª vence ${fmtVenc(boleto.vencimento)}` : `vence ${fmtVenc(boleto.vencimento)}`}</span>
                          </div>
                          {boleto.installmentId && (
                            <div className="text-[10px] text-muted-foreground tabular-nums mt-0.5">total {fmtCurrency(boleto.valorTotal)} · demais parcelas mensais</div>
                          )}
                          <div className="text-[9px] text-muted-foreground/70 mt-1">
                            {boleto.installmentId ? 'Arraste pra conversa pra enviar o CARNÊ (PDF com todos os boletos) ➜' : 'Arraste pra conversa pra enviar o PDF ➜'}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button onClick={() => boletoCopy(`linha-${boleto.paymentId}`, boleto.linhaDigitavel)} disabled={!boleto.linhaDigitavel}
                            title="Copiar linha digitável"
                            className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-foreground hover:bg-secondary disabled:opacity-40">
                            {boletoCopied === `linha-${boleto.paymentId}` ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />} Linha digitável
                          </button>
                          <button onClick={() => boletoCopy(`pix-${boleto.paymentId}`, boleto.pixPayload)} disabled={!boleto.pixPayload}
                            title="Copiar Pix copia-e-cola"
                            className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-foreground hover:bg-secondary disabled:opacity-40">
                            {boletoCopied === `pix-${boleto.paymentId}` ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />} Pix copia-e-cola
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => window.dispatchEvent(new CustomEvent('asaas-boleto-send', { detail: boletoSendPayload(boleto) }))}
                            title="Enviar o PDF do boleto nesta conversa (mesmo efeito do arrastar)"
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-[10px] font-medium px-2 py-1.5 hover:opacity-90">
                            <FiSend className="w-3 h-3" /> Enviar {boleto.rotulo ? boleto.rotulo.toLowerCase() : ''} na conversa
                          </button>
                          {(boleto.invoiceUrl || boleto.bankSlipUrl) && (
                            <a href={boleto.invoiceUrl || boleto.bankSlipUrl || '#'} target="_blank" rel="noreferrer"
                              title="Abrir o boleto no Asaas"
                              className="inline-flex items-center justify-center rounded-md border border-border w-7 h-7 text-muted-foreground hover:text-foreground hover:bg-secondary">
                              <FiExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          <button onClick={() => cancelarBoleto(boleto)} disabled={boletoBusy}
                            title="Cancelar esta cobrança no Asaas"
                            className="inline-flex items-center justify-center rounded-md border border-red-500/40 w-7 h-7 text-red-500 hover:bg-red-500/10 disabled:opacity-40">
                            {boletoBusy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiTrash2 className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => { setGerados([]); setBoletoError(null); valorTocadoRef.current = true; setBoletoForm(f => ({ ...f, valor: '', entrada: '', descricao: '', parcelas: '1' })) }}
                      title="Manter estes boletos no Asaas e preparar uma nova negociação"
                      className="w-full text-[10px] text-muted-foreground hover:text-foreground underline decoration-dotted">
                      Nova negociação
                    </button>
                  </>
                )}

                {/* ── Boletos JÁ emitidos no Asaas (compartilhado entre admins:
                    quem gerou não importa). Parcelamento agrupa em carnê. ── */}
                {(cobrancasLoading || cobrancasErro || (cobrancas && cobrancas.length > 0)) && (
                  <div className="rounded-md border border-border bg-card px-2 py-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[9px] uppercase tracking-wider">
                      <span className="font-semibold text-muted-foreground">Boletos emitidos (Asaas)</span>
                      {cobrancas && <span className="text-muted-foreground">{cobrancas.length}</span>}
                    </div>
                    {cobrancasLoading && !cobrancas && (
                      <div className="flex items-center gap-2 py-1 text-[10px] text-muted-foreground">
                        <FiLoader className="w-3 h-3 animate-spin" /> Buscando no Asaas…
                      </div>
                    )}
                    {/* Consulta falhou: dizer isso em voz alta. Silenciar aqui é o
                        que faz alguém emitir boleto repetido achando que não saiu. */}
                    {cobrancasErro && !cobrancasLoading && (
                      <div className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-1 text-[10px] text-amber-700 dark:text-amber-400 space-y-0.5">
                        <div className="font-semibold">Não foi possível consultar o Asaas.</div>
                        <div className="opacity-80 break-words">{cobrancasErro}</div>
                        <div className="opacity-80">
                          Esta lista pode estar incompleta — <b>confira no Asaas antes de emitir outro boleto</b>.
                        </div>
                        <button
                          onClick={() => { const d = cobrancasDocRef.current; if (d) loadCobrancas(d) }}
                          className="underline hover:no-underline">Tentar de novo</button>
                      </div>
                    )}
                    {(() => {
                      if (!cobrancas) return null
                      const chip = (s: string) => {
                        const st = COBRANCA_STATUS[s] || { label: s, cls: 'bg-secondary text-muted-foreground' }
                        return <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold shrink-0 ${st.cls}`}>{st.label}</span>
                      }
                      const byIns = new Map<string, AsaasCobranca[]>()
                      const avulsos: AsaasCobranca[] = []
                      for (const c of cobrancas) {
                        if (c.installmentId) {
                          if (!byIns.has(c.installmentId)) byIns.set(c.installmentId, [])
                          byIns.get(c.installmentId)!.push(c)
                        } else avulsos.push(c)
                      }
                      const linha = (payload: AsaasBoletoSendPayload, left: ReactNode, statusEl: ReactNode, url: string | null) => (
                        <div draggable
                          onDragStart={e => { e.dataTransfer.setData(ASAAS_BOLETO_MIME, JSON.stringify(payload)); e.dataTransfer.effectAllowed = 'copy' }}
                          className="flex items-center gap-2 text-[10px] rounded px-1 -mx-1 hover:bg-secondary/50 cursor-grab active:cursor-grabbing"
                          title="Arraste pra conversa pra enviar o PDF, ou use os botões">
                          {left}
                          <span className="flex-1" />
                          {statusEl}
                          <button onClick={() => window.dispatchEvent(new CustomEvent('asaas-boleto-send', { detail: payload }))}
                            title="Enviar o PDF nesta conversa"
                            className="w-5 h-5 flex items-center justify-center rounded text-primary hover:bg-primary/10 shrink-0">
                            <FiSend className="w-3 h-3" />
                          </button>
                          {url && (
                            <a href={url} target="_blank" rel="noreferrer" title="Abrir no Asaas"
                              className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-secondary shrink-0">
                              <FiExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      )
                      return (
                        <>
                          {[...byIns.entries()].map(([insId, parcels]) => {
                            const sorted = [...parcels].sort((a, b) => a.vencimento.localeCompare(b.vencimento))
                            const n = sorted.length
                            const pagas = sorted.filter(c => ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(c.status)).length
                            const first = sorted[0]
                            const payload: AsaasBoletoSendPayload = {
                              paymentId: first.paymentId, installmentId: insId,
                              fileName: `carne-${effCode || 'cliente'}-${n}x.pdf`,
                              caption: `Carnê ${n}× de ${fmtCurrency(first.valor)} · 1ª parcela vence ${fmtVenc(first.vencimento)}`,
                            }
                            return (
                              <div key={insId}>
                                {linha(payload,
                                  <span className="text-foreground tabular-nums shrink-0">Carnê {n}× de {fmtCurrency(first.valor)}</span>,
                                  <span className="text-[9px] text-muted-foreground shrink-0 tabular-nums">{pagas}/{n} pagas</span>,
                                  first.invoiceUrl || first.bankSlipUrl)}
                              </div>
                            )
                          })}
                          {avulsos.map(c => {
                            const payload: AsaasBoletoSendPayload = {
                              paymentId: c.paymentId,
                              fileName: `boleto-${effCode || 'cliente'}-${c.vencimento}.pdf`,
                              caption: `Boleto ${fmtCurrency(c.valor)} · vence ${fmtVenc(c.vencimento)}`,
                            }
                            return (
                              <div key={c.paymentId}>
                                {linha(payload,
                                  <span className="text-foreground tabular-nums shrink-0">{fmtCurrency(c.valor)} · {fmtVenc(c.vencimento)}</span>,
                                  chip(c.status),
                                  c.invoiceUrl || c.bankSlipUrl)}
                              </div>
                            )
                          })}
                        </>
                      )
                    })()}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ─── LEADS ENCAMINHADOS ─── Só aparece se o contato é líder COM leads
             pendentes de baixa (self-gating pelo endpoint by-leader). Fallback
             manual: status + comentário + botões de baixa, sem sair da conversa. */}
        {SHOW_CRM_SECTIONS && leaderCounts && leaderCounts.pending > 0 && (
          <>
            <button onClick={() => toggleSection('leads')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('leads') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <FiAward className="w-3 h-3 text-purple-500" /> LEADS ENCAMINHADOS
              <span className="ml-auto text-[9px] bg-purple-500/15 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full tabular-nums">{leaderCounts.pending} p/ baixa</span>
            </button>
            {expandedSections.has('leads') && (
              <div className="px-3 py-2 space-y-2 border-b border-border/50">
                {leaderLeadsLoading && (
                  <div className="flex items-center justify-center gap-2 py-2 text-[11px] text-muted-foreground">
                    <FiLoader className="w-3.5 h-3.5 animate-spin" /> Atualizando…
                  </div>
                )}
                {leadError && (
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <FiAlertCircle className="w-3 h-3 shrink-0" /> {leadError}
                  </div>
                )}
                {leadInfo && (
                  <div className="text-[10px] text-sky-600 dark:text-sky-400 flex items-center gap-1.5">
                    <FiSend className="w-3 h-3 shrink-0" /> {leadInfo}
                  </div>
                )}
                {leaderLeads.filter(l => showConcludedLeads || l.status !== 'concluded').map(l => {
                  const sm = LEAD_STATUS_META[l.status]
                  const action = nextLeadAction(l)
                  const busy = leadActionBusy === l.assignmentId
                  return (
                    <div key={l.assignmentId} className="rounded-md border border-border bg-card px-2.5 py-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-medium text-foreground truncate">{l.leadName}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{l.leadPhone}</div>
                        </div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${sm.cls}`}>{sm.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px]">
                        {/* Próxima ação automática enquanto aguarda retorno (open/overdue):
                            lembrete 12h → aviso de prazo 24h → expirado. Quando a líder já
                            respondeu "tentando" (acknowledged), o prazo não corre mais. */}
                        {action && (
                          <span className={`inline-flex items-center gap-1 tabular-nums ${action.cls}`}
                            title={l.deadlineAt ? `Prazo final: ${new Date(l.deadlineAt).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande' })}` : ''}>
                            <FiClock className="w-3 h-3" />{action.text}
                          </span>
                        )}
                        {l.status === 'acknowledged' && (
                          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <FiClock className="w-3 h-3" />ainda tentando
                          </span>
                        )}
                        {l.convertedOracle && <span className="text-purple-600 dark:text-purple-400 font-bold">✦ Convertido</span>}
                        {l.nudgeCount > 0 && <span className="text-muted-foreground/70">{l.nudgeCount} lembrete{l.nudgeCount === 1 ? '' : 's'}</span>}
                      </div>
                      {l.status !== 'concluded' ? (
                        <div className="mt-2 space-y-1.5">
                          <input
                            value={leadReason[l.assignmentId] || ''}
                            onChange={e => setLeadReason(s => ({ ...s, [l.assignmentId]: e.target.value }))}
                            placeholder="Comentário (opcional)"
                            disabled={busy}
                            className="w-full h-7 text-[11px] rounded-md border border-border bg-background px-2 text-foreground placeholder:text-muted-foreground/70"
                          />
                          <div className="grid grid-cols-3 gap-1">
                            <button disabled={busy} title="Deu certo"
                              onClick={() => leadAction(l.assignmentId, 'outcome', { outcome: 'success', reason: (leadReason[l.assignmentId] || '').trim() || undefined })}
                              className="h-7 text-[10px] font-medium rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors">Deu certo</button>
                            <button disabled={busy} title="Não deu certo"
                              onClick={() => leadAction(l.assignmentId, 'outcome', { outcome: 'failure', reason: (leadReason[l.assignmentId] || '').trim() || undefined })}
                              className="h-7 text-[10px] font-medium rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 disabled:opacity-40 transition-colors">Não deu</button>
                            <button disabled={busy} title="Ainda tentando"
                              onClick={() => leadAction(l.assignmentId, 'outcome', { outcome: 'trying', reason: (leadReason[l.assignmentId] || '').trim() || undefined })}
                              className="h-7 text-[10px] font-medium rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition-colors">Tentando</button>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {l.autoExpiry && l.status === 'overdue' ? (
                              <button disabled={busy}
                                onClick={() => leadAction(l.assignmentId, 'notify-expired', {})}
                                title="Avisar a líder que o prazo de 24h expirou (template de utilidade). Pinga a líder e sobe a conversa no topo do inbox."
                                className="h-7 text-[10px] font-medium rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1">
                                <FiClock className="w-3 h-3" /> Notificar expirado
                              </button>
                            ) : (
                              <button disabled={busy}
                                onClick={() => leadAction(l.assignmentId, 'resend-nudge', {})}
                                title="Reenviar a cobrança de status à líder (com os botões Deu certo/Não deu/Tentando). Pinga a líder e sobe a conversa no topo do inbox."
                                className="h-7 text-[10px] font-medium rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:bg-sky-500/20 disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1">
                                <FiSend className="w-3 h-3" /> Reenviar cobrança
                              </button>
                            )}
                            <button disabled={busy}
                              onClick={() => setReassignLead(l)}
                              title="Encaminhar este lead a OUTRA líder. Reinicia o relógio de 24h para a nova líder."
                              className="h-7 text-[10px] font-medium rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1">
                              <FiRepeat className="w-3 h-3" /> Trocar líder
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 space-y-1.5">
                          <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
                            {l.leaderOutcome === 'success' ? <FiCheck className="w-3 h-3 text-emerald-500" /> : l.leaderOutcome === 'failure' ? <FiX className="w-3 h-3 text-rose-500" /> : null}
                            {l.leaderOutcome === 'success' ? 'Deu certo' : l.leaderOutcome === 'failure' ? 'Não deu certo' : 'Concluído'}
                            {l.leaderOutcomeReason ? ` · ${l.leaderOutcomeReason}` : ''}
                            <button disabled={busy} onClick={() => toggleEditOutcome(l.assignmentId)}
                              title="Mudar o desfecho (ex.: cliente desistiu depois de marcado como deu certo)"
                              className="ml-1 text-sky-600 dark:text-sky-400 hover:underline disabled:opacity-40">
                              {editingOutcome.has(l.assignmentId) ? 'cancelar' : 'alterar'}
                            </button>
                          </div>
                          {editingOutcome.has(l.assignmentId) && (
                            <>
                              <input
                                value={leadReason[l.assignmentId] || ''}
                                onChange={e => setLeadReason(s => ({ ...s, [l.assignmentId]: e.target.value }))}
                                placeholder="Motivo da mudança (opcional — ex.: cliente desistiu)"
                                disabled={busy}
                                className="w-full h-7 text-[11px] rounded-md border border-border bg-background px-2 text-foreground placeholder:text-muted-foreground/70"
                              />
                              <div className="grid grid-cols-3 gap-1">
                                <button disabled={busy} title="Deu certo"
                                  onClick={() => { leadAction(l.assignmentId, 'outcome', { outcome: 'success', reason: (leadReason[l.assignmentId] || '').trim() || undefined }); toggleEditOutcome(l.assignmentId) }}
                                  className="h-7 text-[10px] font-medium rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors">Deu certo</button>
                                <button disabled={busy} title="Não deu certo"
                                  onClick={() => { leadAction(l.assignmentId, 'outcome', { outcome: 'failure', reason: (leadReason[l.assignmentId] || '').trim() || undefined }); toggleEditOutcome(l.assignmentId) }}
                                  className="h-7 text-[10px] font-medium rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 disabled:opacity-40 transition-colors">Não deu</button>
                                <button disabled={busy} title="Ainda tentando (reabre o acompanhamento)"
                                  onClick={() => { leadAction(l.assignmentId, 'outcome', { outcome: 'trying', reason: (leadReason[l.assignmentId] || '').trim() || undefined }); toggleEditOutcome(l.assignmentId) }}
                                  className="h-7 text-[10px] font-medium rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition-colors">Tentando</button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
                {leaderCounts.concluded > 0 && (
                  <button onClick={() => setShowConcludedLeads(v => !v)} className="text-[10px] text-muted-foreground hover:text-foreground underline">
                    {showConcludedLeads ? 'Ocultar concluídos' : `Ver ${leaderCounts.concluded} concluído${leaderCounts.concluded === 1 ? '' : 's'}`}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Trocar líder a partir da sidebar: reaproveita o mesmo modal do inbox/painel.
          conversationId/deviceId vêm do lead (não da líder em foco). */}
      {reassignLead && (
        <AssignLeaderModal
          isOpen={!!reassignLead}
          onClose={() => setReassignLead(null)}
          conversationId={reassignLead.conversationId}
          deviceId={reassignLead.deviceId}
          contactName={reassignLead.leadName}
          contactPhone={reassignLead.leadPhone}
          onAssign={() => { setReassignLead(null); if (profile?.code) loadLeaderLeads(profile.code) }}
        />
      )}
    </div>
  )
}
